/* eslint-disable max-statements */
import { ActionType, AppError, EipError } from './types';
import * as ethereum from 'src/utils/ethereum';
import * as api from 'src/redux/api';
import { getSpecialEventRewards, getUserRanking, postJoinSpecialEvent } from 'src/redux/api';
import { MKPRefType } from 'src/models';
import { getGiftCode } from 'src/utils/session';
import { ITEM_PER_PAGE_DESKTOP as ITEM_PER_PAGE } from 'src/Pages/MarketplaceV2/Components/MarketHero/Components/SearchResult/constants';
import { THETAN_COIN_CONTRACT_ADDRESS } from 'src/constants';
import { contractAddress, MarketType } from 'src/utils/types';
import { systemCurrencyToBlockchainNumber, systemCurrencyToServerCurrency } from '@wolffun/system-currency';
import { NFT_CONTRACT_ADDRESS, COSMETIC_NFT_CONTRACT_ADDRESS } from '@wolffun/contract-address';

const updateSearchParams = searchParams => ({
  type: ActionType.UPDATE_SEARCH_PARAMS,
  payload: {
    searchParams
  }
});

export const search = searchParams => async dispatch => {
  const params = { ...searchParams,
    size: searchParams.size || ITEM_PER_PAGE
  };
  dispatch(updateSearchParams(params));
  return await dispatch(fetchMarketplaceResult(params));
};

const fetchMarketplaceResultRequested = searchParams => ({
  type: ActionType.FETCH_SEARCH_RESULT_REQUESTED,
  payload: {
    searchParams
  }
});

const fetchMarketplaceResultSuccess = (searchParams, data, totalItems) => ({
  type: ActionType.FETCH_SEARCH_RESULT_SUCCESS,
  payload: {
    searchParams,
    data,
    totalItems
  }
});

const fetchMarketplaceResultFailure = (searchParams, error) => ({
  type: ActionType.FETCH_SEARCH_RESULT_FAILURE,
  payload: {
    searchParams,
    error
  }
});

export const fetchMarketplaceResult = searchParams => async dispatch => {
  dispatch(fetchMarketplaceResultRequested(searchParams));
  const response = await api.searchNftItem(searchParams);

  if (response.status === 'error') {
    dispatch(fetchMarketplaceResultFailure(searchParams, response.error));
    return undefined;
  }

  const {
    totalItems,
    data: items
  } = response.body;
  dispatch(fetchMarketplaceResultSuccess(searchParams, items, totalItems));
  return items;
};

const updateSearchRentalParams = searchParams => ({
  type: ActionType.UPDATE_SEARCH_RENTAL_PARAMS,
  payload: {
    searchParams
  }
});

export const searchRental = searchParams => async dispatch => {
  const params = { ...searchParams,
    size: searchParams.size || ITEM_PER_PAGE
  };
  dispatch(updateSearchRentalParams(params));
  return await dispatch(fetchRentalResult(params));
};

const fetchRentalResultRequested = searchParams => ({
  type: ActionType.FETCH_SEARCH_RENTAL_RESULT_REQUESTED,
  payload: {
    searchParams
  }
});

const fetchRentalResultSuccess = (searchParams, data, totalItems) => ({
  type: ActionType.FETCH_SEARCH_RENTAL_RESULT_SUCCESS,
  payload: {
    searchParams,
    data,
    totalItems
  }
});

const fetchRentalResultFailure = (searchParams, error) => ({
  type: ActionType.FETCH_SEARCH_RENTAL_RESULT_FAILURE,
  payload: {
    searchParams,
    error
  }
});

export const fetchRentalResult = searchParams => async dispatch => {
  dispatch(fetchRentalResultRequested(searchParams));
  const response = await api.searchHeroRental({ ...searchParams,
    type: MarketType.FOR_RENT
  });

  if (response.status === 'error') {
    dispatch(fetchRentalResultFailure(searchParams, response.error));
    return undefined;
  }

  const {
    totalItems,
    data: items
  } = response.body;
  dispatch(fetchRentalResultSuccess(searchParams, items, totalItems));
  return items;
};
export const searchAvailableHeroInventory = searchParams => async dispatch => {
  return await dispatch(fetchAvailableHeroInventoryResult(searchParams));
};

const fetchAvailableHeroInventoryResultRequested = () => ({
  type: ActionType.FETCH_AVAILABLE_HERO_INVENTORY_RESULT_REQUESTED,
  payload: undefined
});

const fetchAvailableHeroInventoryResultSuccess = (searchParams, data, totalItems) => ({
  type: ActionType.FETCH_AVAILABLE_HERO_INVENTORY_RESULT_SUCCESS,
  payload: {
    searchParams,
    data,
    totalItems
  }
});

const fetchAvailableHeroInventoryResultFailure = error => ({
  type: ActionType.FETCH_AVAILABLE_HERO_INVENTORY_RESULT_FAILURE,
  payload: {
    error
  }
});

export const fetchAvailableHeroInventoryResult = searchParams => async (dispatch, getState) => {
  dispatch(fetchAvailableHeroInventoryResultRequested());
  const response = await api.searchInventory({ ...searchParams,
    skinVer: getState().entities.skinVer
  });

  if (response.status === 'error') {
    dispatch(fetchAvailableHeroInventoryResultFailure(response.error));
    return undefined;
  }

  const {
    totalItems,
    heroes: items,
    skinConfigs,
    skinVer
  } = response.body;

  if (!!skinConfigs && !!skinVer) {
    dispatch(pushSkinConfigToEntities(skinConfigs));
    dispatch(saveSkinVerToEntities(skinVer));
  }

  dispatch(fetchAvailableHeroInventoryResultSuccess(searchParams, items, totalItems));
  return items;
};

const invalidateHeroDetail = () => ({
  type: ActionType.INVALIDATE_HERO_DETAIL,
  payload: undefined
});

const fetchHeroDetailRequested = id => ({
  type: ActionType.FETCH_HERO_DETAIL_REQUESTED,
  payload: {
    id
  }
});

const fetchHeroDetailSuccess = ({
  heroDetail
}) => ({
  type: ActionType.FETCH_HERO_DETAIL_SUCCESS,
  payload: {
    heroDetail
  }
});

const fetchHeroDetailFailure = error => ({
  type: ActionType.FETCH_HERO_DETAIL_FAILURE,
  payload: {
    error
  }
});

const fetchHeroDescriptionSuccess = (configVer, descriptionConfigs, reward) => ({
  type: ActionType.FETCH_HERO_DESCRIPTION_SUCCESS,
  payload: {
    configVer,
    description: descriptionConfigs,
    reward
  }
});

const fetchHeroDescriptionFailure = error => ({
  type: ActionType.FETCH_HERO_DESCRIPTION_FAILURE,
  payload: {
    error
  }
});

export const fetchHeroDescription = () => async (dispatch, getState) => {
  const heroDescriptionResponse = await api.getHeroDescription({
    configVer: getState().heroDescriptionConfigs.configVer
  });
  if (heroDescriptionResponse.status === 'error') dispatch(fetchHeroDescriptionFailure(heroDescriptionResponse.error));

  if (heroDescriptionResponse.status === 'ok') {
    const {
      configVer,
      descriptionConfigs,
      reward
    } = heroDescriptionResponse.body;

    if (!!configVer && !!descriptionConfigs && !!reward) {
      dispatch(fetchHeroDescriptionSuccess(configVer, descriptionConfigs, reward));
      return descriptionConfigs;
    }
  }

  return undefined;
};

const fetchHeroUpgradeCostRequested = id => ({
  type: ActionType.FETCH_HERO_UPGRADE_COST_REQUESTED,
  payload: {
    id
  }
});

const fetchHeroUpgradeCostSuccess = ({
  data
}) => ({
  type: ActionType.FETCH_HERO_UPGRADE_COST_SUCCESS,
  payload: {
    data
  }
});

const fetchHeroUpgradeCostFailure = error => ({
  type: ActionType.FETCH_HERO_UPGRADE_COST_FAILURE,
  payload: {
    error
  }
});

export const fetchHeroUpgradeCost = heroId => async dispatch => {
  dispatch(fetchHeroUpgradeCostRequested(heroId));
  const heroUpgradeCostResponse = await api.getHeroUpgradeCost({
    heroId
  });
  if (heroUpgradeCostResponse.status === 'error') dispatch(fetchHeroUpgradeCostFailure(heroUpgradeCostResponse.error));

  if (heroUpgradeCostResponse.status === 'ok') {
    const {
      data
    } = heroUpgradeCostResponse.body;

    if (data) {
      dispatch(fetchHeroUpgradeCostSuccess({
        data
      }));
      return data;
    }
  }

  return undefined;
};

const upgradeHeroRequested = id => ({
  type: ActionType.UPGRADE_HERO_REQUESTED,
  payload: {
    id
  }
});

const upgradeHeroSuccess = ({
  id
}) => ({
  type: ActionType.UPGRADE_HERO_SUCCESS,
  payload: {
    successItemId: id
  }
});

const upgradeHeroFailure = error => ({
  type: ActionType.UPGRADE_HERO_FAILURE,
  payload: {
    error
  }
});

export const upgradeHero = ({
  heroId,
  costTHG
}) => async dispatch => {
  dispatch(upgradeHeroRequested(heroId));
  const upgradeHeroResponse = await api.upgradeHero({
    heroId,
    costTHG: systemCurrencyToServerCurrency(costTHG)
  });

  if (upgradeHeroResponse.status === 'error') {
    dispatch(upgradeHeroFailure(upgradeHeroResponse.error));
    return {
      error: upgradeHeroResponse.error
    };
  }

  const {
    id
  } = upgradeHeroResponse.body;
  dispatch(upgradeHeroSuccess({
    id
  }));
  return {
    id
  };
};

const fetchRentalConfigSuccess = (configVer, data) => ({
  type: ActionType.FETCH_RENTAL_CONFIG_SUCCESS,
  payload: {
    configVer,
    data
  }
});

const fetchRentalConfigFailure = error => ({
  type: ActionType.FETCH_HERO_DESCRIPTION_FAILURE,
  payload: {
    error
  }
});

export const fetchRentalConfig = () => async (dispatch, getState) => {
  const heroDescriptionResponse = await api.getRentalConfig({
    ver: getState().itemRentalConfigs.configVer
  });
  if (heroDescriptionResponse.status === 'error') dispatch(fetchRentalConfigFailure(heroDescriptionResponse.error));

  if (heroDescriptionResponse.status === 'ok') {
    const {
      configVer,
      data
    } = heroDescriptionResponse.body;

    if (!!configVer && !!data) {
      dispatch(fetchRentalConfigSuccess(configVer, data));
      return data;
    }
  }

  return undefined;
};

const fetchMintConfigSuccess = data => ({
  type: ActionType.FETCH_MINT_CONFIG_SUCCESS,
  payload: {
    data
  }
});

const fetchMintConfigFailure = error => ({
  type: ActionType.FETCH_MINT_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const fetchMintConfig = () => async dispatch => {
  const res = await api.getMintConfig({});

  if (res.status === 'error') {
    dispatch(fetchMintConfigFailure(res.error));
    return {
      error: res.error
    };
  }

  dispatch(fetchMintConfigSuccess(res.body.data));
  return {
    data: res.body.data
  };
};
export const invalidateRentOutItem = () => ({
  type: ActionType.INVALIDATE_RENT_OUT_ITEM,
  payload: undefined
});

const rentOutItemRequested = () => ({
  type: ActionType.RENT_OUT_ITEM_REQUESTED,
  payload: undefined
});

const rentOutItemSuccess = () => ({
  type: ActionType.RENT_OUT_ITEM_SUCCESS,
  payload: undefined
});

const rentOutItemFailure = error => ({
  type: ActionType.RENT_OUT_ITEM_FAILURE,
  payload: {
    error
  }
});

export const prepareRentOutItem = params => async dispatch => {
  dispatch(invalidateRentOutItem());
  dispatch(rentOutItemRequested());
  const {
    rentPrice,
    tokenId,
    lockDuration,
    numberBattle
  } = params;
  const signSignatureRes = await api.signLendingSignature({
    tokenId: Number(tokenId),
    rentBattles: numberBattle,
    price: systemCurrencyToServerCurrency(rentPrice),
    lockDuration
  });

  if (signSignatureRes.status === 'error') {
    dispatch(rentOutItemFailure(signSignatureRes.error));
    return {
      params,
      error: signSignatureRes.error
    };
  }

  return {
    params,
    data: signSignatureRes.body
  };
};
export const rentOutItem = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    rentOutItemFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(rentOutItemFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    blockchainId,
    expiredAt,
    price,
    signature,
    tokenId: _tokenId
  } = preparedData;
  const {
    error
  } = await ethereum.lending({
    senderAddress: accounts[0],
    blockchainId,
    expiredAt,
    systemPrice: price,
    signature,
    tokenId: _tokenId
  });

  if (error !== EipError.NO_ERROR) {
    dispatch(rentOutItemFailure(error));
    return {
      error: error
    };
  }

  dispatch(rentOutItemSuccess());
  return {};
};
export const invalidateStopRentOutItem = () => ({
  type: ActionType.INVALIDATE_STOP_RENT_OUT_ITEM,
  payload: undefined
});

const stopRentOutItemRequested = () => ({
  type: ActionType.STOP_RENT_OUT_ITEM_REQUESTED,
  payload: undefined
});

const stopRentOutItemSuccess = () => ({
  type: ActionType.STOP_RENT_OUT_ITEM_SUCCESS,
  payload: undefined
});

const stopRentOutItemFailure = error => ({
  type: ActionType.STOP_RENT_OUT_ITEM_FAILURE,
  payload: {
    error
  }
});

export const stopRentOutItem = ({
  tokenId
}) => async (dispatch, getState) => {
  dispatch(invalidateStopRentOutItem());
  dispatch(stopRentOutItemRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(stopRentOutItemFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    error: stopSellingError
  } = await ethereum.stopRentOutItem({
    senderAddress: accounts[0],
    tokenId: Number(tokenId)
  });

  if (stopSellingError !== EipError.NO_ERROR) {
    dispatch(stopRentOutItemFailure(stopSellingError));
    return {
      error: stopSellingError
    };
  }

  dispatch(stopRentOutItemSuccess());
  return {};
};
export const invalidateRentItem = () => ({
  type: ActionType.INVALIDATE_RENT_ITEM,
  payload: undefined
});

const rentItemRequested = () => ({
  type: ActionType.RENT_ITEM_REQUESTED,
  payload: undefined
});

const rentItemSuccess = () => ({
  type: ActionType.RENT_ITEM_SUCCESS,
  payload: undefined
});

const rentItemFailure = error => ({
  type: ActionType.RENT_ITEM_FAILURE,
  payload: {
    error
  }
});

export const prepareRentItem = params => async dispatch => {
  dispatch(invalidateRentItem());
  dispatch(rentItemRequested());
  const {
    tokenId,
    renterAddress
  } = params;
  const {
    ownerAddress
  } = await ethereum.getOwnerOfItem(String(tokenId));

  if (ownerAddress && ownerAddress.toLowerCase() !== renterAddress.toLowerCase()) {
    dispatch(rentItemFailure(AppError.HERO_HAS_BEEN_RENTED));
    return {
      error: AppError.HERO_HAS_BEEN_RENTED,
      params
    };
  }

  return {
    params
  };
};
export const rentItem = args => async (dispatch, getState) => {
  const {
    error: preparedError,
    params
  } = args;

  if (preparedError) {
    const _error = preparedError;
    rentItemFailure(_error);
    return {
      error: _error
    };
  }

  const {
    systemPrice,
    tokenId
  } = params;
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(rentItemFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const senderAddress = accounts[0];
  const {
    error: checkoutError,
    data
  } = await ethereum.rentItem({
    price: systemPrice,
    senderAddress,
    tokenId
  });

  if (checkoutError !== EipError.NO_ERROR) {
    dispatch(rentItemFailure(checkoutError));
    return {
      error: checkoutError
    };
  }

  if (!data) {
    dispatch(rentItemFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN
    };
  }

  dispatch(rentItemSuccess());
  return {
    data: {
      id: data
    }
  };
};
export const invalidateUnlockHeroRental = () => ({
  type: ActionType.INVALIDATE_UNLOCK_HERO_RENTAL,
  payload: undefined
});

const unlockHeroRentalRequested = () => ({
  type: ActionType.UNLOCK_HERO_RENTAL_REQUESTED,
  payload: undefined
});

const unlockHeroRentalSuccess = () => ({
  type: ActionType.UNLOCK_HERO_RENTAL_SUCCESS,
  payload: undefined
});

const unlockHeroRentalFailure = error => ({
  type: ActionType.UNLOCK_HERO_RENTAL_FAILURE,
  payload: {
    error
  }
});

export const prepareUnlockHeroRental = params => async dispatch => {
  dispatch(invalidateUnlockHeroRental());
  dispatch(unlockHeroRentalRequested());
  const {
    tokenId
  } = params;
  const signSignatureRes = await api.signUnlockSignature({
    tokenId: Number(tokenId)
  });

  if (signSignatureRes.status === 'error') {
    dispatch(unlockHeroRentalFailure(signSignatureRes.error));
    return {
      error: signSignatureRes.error,
      params
    };
  }

  return {
    data: signSignatureRes.body,
    params
  };
};
export const unlockHeroRental = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    unlockHeroRentalFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(unlockHeroRentalFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const senderAddress = accounts[0];
  const {
    blockchainId,
    expiredAt,
    signature,
    tokenId: _tokenId,
    old
  } = preparedData;
  const {
    error: checkoutError,
    data
  } = await ethereum.unlockHero({
    senderAddress,
    tokenId: _tokenId,
    bid: blockchainId,
    expiredAt,
    signature,
    old
  });

  if (checkoutError !== EipError.NO_ERROR) {
    dispatch(unlockHeroRentalFailure(checkoutError));
    return {
      error: checkoutError
    };
  }

  if (!data) {
    dispatch(unlockHeroRentalFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN
    };
  }

  dispatch(unlockHeroRentalSuccess());
  return {
    data: {
      id: data
    }
  };
};
export const invalidateMintHero = () => ({
  type: ActionType.INVALIDATE_MINT_HERO,
  payload: undefined
});

const mintHeroRequested = () => ({
  type: ActionType.MINT_HERO_REQUESTED,
  payload: undefined
});

const mintHeroSuccess = () => ({
  type: ActionType.MINT_HERO_SUCCESS,
  payload: undefined
});

const mintHeroFailure = error => ({
  type: ActionType.MINT_HERO_FAILURE,
  payload: {
    error
  }
});

export const prepareMintHero = params => async (dispatch, getState) => {
  dispatch(invalidateMintHero());
  dispatch(mintHeroRequested());
  const {
    heroId
  } = params;
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(mintHeroFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN,
      params
    };
  }

  const signSignatureRes = await api.signMintHeroSignature({
    sender: accounts[0],
    heroId: heroId
  });

  if (signSignatureRes.status === 'error') {
    dispatch(mintHeroFailure(signSignatureRes.error));
    return {
      params,
      error: signSignatureRes.error
    };
  }

  return {
    data: signSignatureRes.body,
    params
  };
};
export const mintHero = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    mintHeroFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(mintHeroFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const senderAddress = accounts[0];
  const {
    blockchainId,
    expiredAt,
    signature,
    tokenId: _tokenId
  } = preparedData;
  const {
    error: checkoutError,
    data
  } = await ethereum.mintHero({
    senderAddress,
    tokenId: _tokenId,
    bid: blockchainId,
    expiredAt,
    signature
  });

  if (checkoutError !== EipError.NO_ERROR) {
    dispatch(mintHeroFailure(checkoutError));
    return {
      error: checkoutError
    };
  }

  if (!data) {
    dispatch(mintHeroFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN
    };
  }

  dispatch(mintHeroSuccess());
  return {
    data: {
      id: data
    }
  };
};
export const invalidateMintCosmetic = () => ({
  type: ActionType.INVALIDATE_MINT_COSMETIC,
  payload: undefined
});

const mintCosmeticRequested = () => ({
  type: ActionType.MINT_COSMETIC_REQUESTED,
  payload: undefined
});

const mintCosmeticSuccess = () => ({
  type: ActionType.MINT_COSMETIC_SUCCESS,
  payload: undefined
});

const mintCosmeticFailure = error => ({
  type: ActionType.MINT_COSMETIC_FAILURE,
  payload: {
    error
  }
});

export const prepareMintCosmetic = params => async (dispatch, getState) => {
  dispatch(invalidateMintCosmetic());
  dispatch(mintCosmeticRequested());
  const {
    cosmeticId
  } = params;
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(mintCosmeticFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN,
      params
    };
  }

  const signSignatureRes = await api.signMintCosmeticSignature({
    sender: accounts[0],
    cosmeticId
  });

  if (signSignatureRes.status === 'error') {
    dispatch(mintCosmeticFailure(signSignatureRes.error));
    return {
      error: signSignatureRes.error,
      params
    };
  }

  return {
    data: signSignatureRes.body,
    params
  };
};
export const mintCosmetic = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    mintCosmeticFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(mintCosmeticFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const senderAddress = accounts[0];
  const {
    blockchainId,
    expiredAt,
    signature,
    tokenId: _tokenId
  } = preparedData;
  const {
    error: checkoutError,
    data
  } = await ethereum.mintCosmetic({
    senderAddress,
    tokenId: _tokenId,
    bid: blockchainId,
    expiredAt,
    signature
  });

  if (checkoutError !== EipError.NO_ERROR) {
    dispatch(mintCosmeticFailure(checkoutError));
    return {
      error: checkoutError
    };
  }

  if (!data) {
    dispatch(mintCosmeticFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN
    };
  }

  dispatch(mintCosmeticSuccess());
  return {
    data: {
      id: data
    }
  };
};

const fetchSkinConfigsRequested = () => ({
  type: ActionType.FETCH_SKIN_CONFIGS_REQUESTED,
  payload: undefined
});

const fetchSkinConfigsSuccess = skinConfigs => ({
  type: ActionType.FETCH_SKIN_CONFIGS_SUCCESS,
  payload: {
    data: skinConfigs === null || skinConfigs === void 0 ? void 0 : skinConfigs.reduce((prev, config) => {
      prev[config.id] = config;
      return prev;
    }, {})
  }
});

const fetchSkinConfigsFailure = error => ({
  type: ActionType.FETCH_SKIN_CONFIGS_FAILURE,
  payload: {
    error
  }
});

export const fetchSkinConfigs = () => async (dispatch, getState) => {
  const prevSkinConfigs = getState().entities.skinConfig;
  if (prevSkinConfigs) return undefined;
  dispatch(fetchSkinConfigsRequested());
  const skinConfigsResponse = await api.getSkinConfigs({});

  if (skinConfigsResponse.status === 'error') {
    dispatch(fetchSkinConfigsFailure(skinConfigsResponse.error));
    return undefined;
  }

  const {
    skinConfigs
  } = skinConfigsResponse.body;

  if (skinConfigs) {
    dispatch(fetchSkinConfigsSuccess(skinConfigs));
    return skinConfigs;
  }

  return undefined;
};
export const fetchHeroDetail = id => async dispatch => {
  dispatch(invalidateHeroDetail());
  dispatch(fetchHeroDetailRequested(id));
  const heroInfoResponse = await api.getHeroInfo({
    id
  });

  if (heroInfoResponse.status === 'error') {
    dispatch(fetchHeroDetailFailure(heroInfoResponse.error));
    return undefined;
  }

  const result = {
    heroDetail: heroInfoResponse.body.item
  };
  dispatch(fetchHeroDetailSuccess(result));
  dispatch(fetchHeroDescription());
  return result;
};

const fetchTransactionConfirmRequested = () => ({
  type: ActionType.FETCH_TRANSACTION_CONFIRM_REQUESTED,
  payload: undefined
});

const fetchTransactionConfirmSuccess = ({
  feeInPercent
}) => ({
  type: ActionType.FETCH_TRANSACTION_CONFIRM_SUCCESS,
  payload: {
    feeInPercent
  }
});

const fetchTransactionConfirmFailure = error => ({
  type: ActionType.FETCH_TRANSACTION_CONFIRM_FAILURE,
  payload: {
    error
  }
});

export const fetchTransactionConfirm = () => async dispatch => {
  dispatch(fetchTransactionConfirmRequested());
  const transactionFeeResponse = await api.getItemTransactionFee({});

  if (transactionFeeResponse.status === 'error') {
    dispatch(fetchTransactionConfirmFailure(transactionFeeResponse.error));
    return;
  }

  dispatch(fetchTransactionConfirmSuccess({
    feeInPercent: transactionFeeResponse.body.sale
  }));
};
export const invalidateCheckoutItem = () => ({
  type: ActionType.INVALIDATE_CHECKOUT_ITEM,
  payload: undefined
});

const checkoutItemRequested = () => ({
  type: ActionType.CHECKOUT_ITEM_REQUESTED,
  payload: undefined
});

const checkoutItemSuccess = id => ({
  type: ActionType.CHECKOUT_ITEM_SUCCESS,
  payload: {
    id
  }
});

const checkoutItemFailure = error => ({
  type: ActionType.CHECKOUT_ITEM_FAILURE,
  payload: {
    error
  }
});

export const checkoutItem = ({
  systemPrice,
  blockchainId
}) => async (dispatch, getState) => {
  dispatch(invalidateCheckoutItem());
  dispatch(checkoutItemRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(checkoutItemFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    error: checkoutError,
    data
  } = await ethereum.checkoutItem({
    price: systemPrice,
    senderAddress: accounts[0],
    blockchainId,
    paymentTokenAddress: THETAN_COIN_CONTRACT_ADDRESS
  });

  if (checkoutError !== EipError.NO_ERROR) {
    dispatch(checkoutItemFailure(checkoutError));
    return {
      error: checkoutError
    };
  }

  if (!data) {
    dispatch(checkoutItemFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN
    };
  }

  dispatch(checkoutItemSuccess(data));
  return {
    data: {
      id: data
    }
  };
};

const getEstimateAprRequested = () => ({
  type: ActionType.FETCH_ESTIMATE_APR_REQUESTED,
  payload: undefined
});

const getEstimateAprSuccess = data => ({
  type: ActionType.FETCH_ESTIMATE_APR_SUCCESS,
  payload: {
    data
  }
});

const getEstimateAprFailure = error => ({
  type: ActionType.FETCH_ESTIMATE_APR_FAILURE,
  payload: {
    error
  }
});

export const getEstimateApr = () => async dispatch => {
  dispatch(getEstimateAprRequested());
  const response = await api.getEstimateApr({});

  if (response.status === 'error') {
    dispatch(getEstimateAprFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(getEstimateAprSuccess(response.body.data));
  return {
    data: response.body.data
  };
};

const unStakingRequested = () => ({
  type: ActionType.UNSTAKING_REQUESTED,
  payload: undefined
});

const unStakingSuccess = success => ({
  type: ActionType.UNSTAKING_SUCCESS,
  payload: {
    success
  }
});

const unStakingFailure = error => ({
  type: ActionType.UNSTAKING_FAILURE,
  payload: {
    error
  }
});

const invalidatedUnStaking = () => ({
  type: ActionType.INVALIDATED_UNSTAKING,
  payload: undefined
});

export const prepareUnStaking = params => async dispatch => {
  dispatch(invalidatedUnStaking());
  dispatch(unStakingRequested());
  const {
    stakingId
  } = params;
  const response = await api.postUnStaking({
    stakingId
  });

  if (response.status === 'error') {
    dispatch(unStakingFailure(response.error));
    return {
      error: response.error,
      params
    };
  }

  return {
    data: response.body,
    params
  };
};
export const unStaking = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError,
    params
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    unStakingFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(unStakingFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN,
      success: false
    };
  }

  const {
    error: unStakingError
  } = await ethereum.unStaking({
    expiredAt: preparedData.signatureExpiredAt,
    signature: preparedData.signature,
    id: params.blockchainId,
    senderAddress: accounts[0]
  });

  if (unStakingError) {
    dispatch(unStakingFailure(unStakingError));
    return {
      success: false,
      error: unStakingError
    };
  }

  dispatch(unStakingSuccess(true));
  return {
    success: true
  };
};

const claimStakingRewardRequested = () => ({
  type: ActionType.CLAIM_STAKING_REWARD_REQUESTED,
  payload: undefined
});

const claimStakingRewardSuccess = success => ({
  type: ActionType.CLAIM_STAKING_REWARD_SUCCESS,
  payload: {
    success
  }
});

const claimStakingRewardFailure = error => ({
  type: ActionType.CLAIM_STAKING_REWARD_FAILURE,
  payload: {
    error
  }
});

const invalidatedClaimStakingReward = () => ({
  type: ActionType.INVALIDATED_CLAIM_STAKING_REWARD,
  payload: undefined
});

export const claimStakingReward = stakingId => async dispatch => {
  dispatch(invalidatedClaimStakingReward());
  dispatch(claimStakingRewardRequested());
  const response = await api.postClaimStakingReward({
    stakingId
  });

  if (response.status === 'error') {
    dispatch(claimStakingRewardFailure(response.error));
    return {
      error: response.error,
      success: false
    };
  }

  dispatch(claimStakingRewardSuccess(true));
  return {
    success: true
  };
};

const getStakingConfigRequested = () => ({
  type: ActionType.STAKING_CONFIG_REQUESTED,
  payload: undefined
});

const getStakingConfigSuccess = data => ({
  type: ActionType.STAKING_CONFIG_SUCCESS,
  payload: {
    data
  }
});

const getStakingConfigFailure = error => ({
  type: ActionType.STAKING_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const getStakingConfig = () => async (dispatch, getState) => {
  dispatch(getStakingConfigRequested());
  const response = await api.getStakingConfig({
    version: getState().stakingConfig.configVer
  });

  if (response.status === 'error') {
    dispatch(getStakingConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      data
    } = response.body;

    if (data) {
      dispatch(getStakingConfigSuccess(data));
      return {
        data: data
      };
    }

    return {
      data: undefined
    };
  }

  return {
    error: AppError.UNNKOWN
  };
};

const getProjectDetailRequested = () => ({
  type: ActionType.FETCH_PROJECT_DETAIL_REQUESTED,
  payload: undefined
});

const getProjectDetailSuccess = data => ({
  type: ActionType.FETCH_PROJECT_DETAIL_SUCCESS,
  payload: {
    data
  }
});

const getProjectDetailFailure = error => ({
  type: ActionType.FETCH_PROJECT_DETAIL_FAILURE,
  payload: {
    error
  }
});

export const getProjectDetail = id => async dispatch => {
  dispatch(getProjectDetailRequested());
  const response = await api.getProjectDetail({
    id
  });

  if (response.status === 'error') {
    dispatch(getProjectDetailFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      data
    } = response.body;

    if (data) {
      dispatch(getProjectDetailSuccess(data));
      return {
        data: data
      };
    }

    dispatch(getProjectDetailFailure(AppError.UNNKOWN));
    return {
      data: undefined
    };
  }

  dispatch(getProjectDetailFailure(AppError.UNNKOWN));
  return {
    error: AppError.UNNKOWN
  };
};

const getPrivateSaleConfigRequested = () => ({
  type: ActionType.FETCH_PRIVATE_SALE_CONFIG_REQUESTED,
  payload: undefined
});

const getPrivateSaleConfigSuccess = youtubeURL => ({
  type: ActionType.FETCH_PRIVATE_SALE_CONFIG_SUCCESS,
  payload: {
    youtubeURL
  }
});

const getPrivateSaleConfigFailure = error => ({
  type: ActionType.FETCH_PRIVATE_SALE_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const getPrivateSaleConfig = () => async dispatch => {
  dispatch(getPrivateSaleConfigRequested());
  const response = await api.getPrivateSaleConfig({});

  if (response.status === 'error') {
    dispatch(getPrivateSaleConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      youtubeURL
    } = response.body;

    if (youtubeURL) {
      dispatch(getPrivateSaleConfigSuccess(youtubeURL));
      return {
        youtubeURL
      };
    }

    dispatch(getPrivateSaleConfigFailure(AppError.UNNKOWN));
    return {
      youtubeURL: undefined
    };
  }

  dispatch(getPrivateSaleConfigFailure(AppError.UNNKOWN));
  return {
    error: AppError.UNNKOWN
  };
};

const getThcExchangeRateRequested = () => ({
  type: ActionType.FETCH_THC_EXCHANGE_RATE_REQUESTED,
  payload: undefined
});

const getThcExchangeRateSuccess = data => ({
  type: ActionType.FETCH_THC_EXCHANGE_RATE_SUCCESS,
  payload: {
    data
  }
});

const getThcExchangeRateFailure = error => ({
  type: ActionType.FETCH_THC_EXCHANGE_RATE_FAILURE,
  payload: {
    error
  }
});

export const getThcExchangeRate = () => async dispatch => {
  dispatch(getThcExchangeRateRequested());
  const response = await api.getThcExchangeRate({});

  if (response.status === 'error') {
    dispatch(getThcExchangeRateFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      data
    } = response.body;
    dispatch(getThcExchangeRateSuccess(data));
    return {
      data: data
    };
  }

  dispatch(getThcExchangeRateFailure(AppError.UNNKOWN));
  return {
    error: AppError.UNNKOWN
  };
};

const buyPrivateSaleRequested = () => ({
  type: ActionType.FETCH_BUY_PRIVATE_SALE_REQUESTED,
  payload: undefined
});

const buyPrivateSaleSuccess = () => ({
  type: ActionType.FETCH_BUY_PRIVATE_SALE_SUCCESS,
  payload: undefined
});

const buyPrivateSaleFailure = error => ({
  type: ActionType.FETCH_BUY_PRIVATE_SALE_FAILURE,
  payload: {
    error
  }
});

export const prepareBuyPrivateSale = params => async dispatch => {
  dispatch(buyPrivateSaleRequested());
  const {
    pTicketAmount,
    projectID,
    thcFee,
    thcPrice,
    otherChainAddress
  } = params;
  const response = await api.postBuyPrivateSale({
    pTicketAmount: systemCurrencyToServerCurrency(pTicketAmount),
    projectID,
    thcFee: systemCurrencyToServerCurrency(thcFee),
    thcPrice: thcPrice,
    otherChainAddress
  });

  if (response.status === 'error') {
    dispatch(buyPrivateSaleFailure(response.error));
    return {
      error: response.error,
      params
    };
  }

  return {
    data: response.body.data,
    params
  };
};
export const buyPrivateSale = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    buyPrivateSaleFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(buyPrivateSaleFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    blockchainID,
    busd,
    expiredAt,
    signature,
    thc
  } = preparedData;
  const {
    error
  } = await ethereum.buyPrivateSale({
    blockChainId: blockchainID,
    busdAmount: systemCurrencyToBlockchainNumber(busd),
    expiredAt,
    signature,
    thcAmount: systemCurrencyToBlockchainNumber(thc),
    buyerAddress: accounts[0]
  });

  if (error !== EipError.NO_ERROR) {
    dispatch(buyPrivateSaleFailure(error));
    return {
      error: error
    };
  }

  dispatch(buyPrivateSaleSuccess());
  return {
    success: true
  };
};

const getStakingStatsRequested = () => ({
  type: ActionType.STAKING_STATS_REQUESTED,
  payload: undefined
});

const getStakingStatsSuccess = data => ({
  type: ActionType.STAKING_STATS_SUCCESS,
  payload: {
    data
  }
});

const getStakingStatsFailure = error => ({
  type: ActionType.STAKING_STATS_FAILURE,
  payload: {
    error
  }
});

export const getStakingStats = () => async dispatch => {
  dispatch(getStakingStatsRequested());
  const response = await api.getStakingStats({});

  if (response.status === 'error') {
    dispatch(getStakingStatsFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      data
    } = response.body;

    if (data) {
      dispatch(getStakingStatsSuccess(data));
      return {
        data: data
      };
    }

    return {
      data: undefined
    };
  }

  return {
    error: AppError.UNNKOWN
  };
};

const getUserProjectRequested = () => ({
  type: ActionType.FETCH_USER_PROJECT_REQUESTED,
  payload: undefined
});

const getUserProjectSuccess = data => ({
  type: ActionType.FETCH_USER_PROJECT_SUCCESS,
  payload: {
    data
  }
});

const getUserProjectFailure = error => ({
  type: ActionType.FETCH_USER_PROJECT_FAILURE,
  payload: {
    error
  }
});

export const invalidateUserProject = () => ({
  type: ActionType.INVALIDATE_USER_PROJECT,
  payload: undefined
});
export const getUserProject = id => async dispatch => {
  dispatch(getUserProjectRequested());
  const response = await api.getUserProject({
    id
  });

  if (response.status === 'error') {
    dispatch(getUserProjectFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      data
    } = response.body;

    if (data) {
      dispatch(getUserProjectSuccess(data));
      return {
        data: data
      };
    }

    return {
      data: undefined
    };
  }

  return {
    error: AppError.UNNKOWN
  };
};

const fetchSaleHistoryListRequested = () => ({
  type: ActionType.FETCH_SALE_HISTORY_LIST_REQUESTED,
  payload: undefined
});

const fetchSaleHistoryListSuccess = ({
  data
}) => ({
  type: ActionType.FETCH_SALE_HISTORY_LIST_SUCCESS,
  payload: {
    data
  }
});

const fetchSaleHistoryListFailure = error => ({
  type: ActionType.FETCH_SALE_HISTORY_LIST_FAILURE,
  payload: {
    error
  }
});

export const resetSaleHistoryList = () => async dispatch => {
  dispatch(fetchSaleHistoryListRequested());
  return;
};
export const fetchSaleHistoryList = searchParams => async dispatch => {
  dispatch(fetchSaleHistoryListRequested());
  const response = await api.getSaleHistoryListBid(searchParams);

  if (response.status === 'error') {
    dispatch(fetchSaleHistoryListFailure(response.error));
    return;
  }

  dispatch(fetchSaleHistoryListSuccess(response.body));
  return;
};
export const invalidateSellItem = () => ({
  type: ActionType.INVALIDATE_SELL_ITEM,
  payload: undefined
});

const sellItemRequested = () => ({
  type: ActionType.SELL_ITEM_REQUESTED,
  payload: undefined
});

const sellItemSuccess = () => ({
  type: ActionType.SELL_ITEM_SUCCESS,
  payload: undefined
});

const sellItemFailure = error => ({
  type: ActionType.SELL_ITEM_FAILURE,
  payload: {
    error
  }
});

export const prepareSellItem = params => async dispatch => {
  dispatch(invalidateSellItem());
  dispatch(sellItemRequested());
  const {
    systemCurrency,
    tokenId
  } = params;
  const signSignatureRes = await api.signSellingSignature({
    nftAddress: NFT_CONTRACT_ADDRESS,
    paymentTokenAddress: THETAN_COIN_CONTRACT_ADDRESS,
    tokenId: tokenId,
    price: systemCurrencyToServerCurrency(systemCurrency)
  });

  if (signSignatureRes.status === 'error') {
    dispatch(sellItemFailure(signSignatureRes.error));
    return {
      error: signSignatureRes.error,
      params
    };
  }

  return {
    data: signSignatureRes.body,
    params
  };
};
export const sellItem = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    sellItemFailure(_error);
    return {
      error: _error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(sellItemFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    blockchainId,
    expiredAt,
    nftAddress,
    paymentTokenAddress,
    price,
    signature,
    tokenId: _tokenId
  } = preparedData;
  const {
    error
  } = await ethereum.sellItem({
    senderAddress: accounts[0],
    blockchainId,
    expiredAt,
    nftAddress,
    paymentTokenAddress,
    systemPrice: price,
    signature,
    tokenId: _tokenId
  });

  if (error !== EipError.NO_ERROR) {
    dispatch(sellItemFailure(error));
    return {
      error: error
    };
  }

  dispatch(sellItemSuccess());
  return {};
};

const fetchSaleItemTransactionConfirmRequested = () => ({
  type: ActionType.FETCH_SALE_ITEM_TRANSACTION_CONFIRM_REQUESTED,
  payload: undefined
});

const fetchSaleItemTransactionConfirmSuccess = ({
  feeInPercent
}) => ({
  type: ActionType.FETCH_SALE_ITEM_TRANSACTION_CONFIRM_SUCCESS,
  payload: {
    feeInPercent
  }
});

const fetchSaleItemTransactionConfirmFailure = error => ({
  type: ActionType.FETCH_SALE_ITEM_TRANSACTION_CONFIRM_FAILURE,
  payload: {
    error
  }
});

export const fetchSaleItemTransactionConfirm = () => async dispatch => {
  dispatch(fetchSaleItemTransactionConfirmRequested());
  const transactionFeeResponse = await api.getItemTransactionFee({});

  if (transactionFeeResponse.status === 'error') {
    dispatch(fetchSaleItemTransactionConfirmFailure(transactionFeeResponse.error));
    return {
      error: transactionFeeResponse.error
    };
  }

  dispatch(fetchSaleItemTransactionConfirmSuccess({
    feeInPercent: transactionFeeResponse.body.sale
  }));
  return undefined;
};

const fetchPaymentTokenRequested = () => ({
  type: ActionType.FETCH_PAYMENT_TOKEN_REQUESTED,
  payload: undefined
});

const fetchPaymentTokenSuccess = ({
  data
}) => ({
  type: ActionType.FETCH_PAYMENT_TOKEN_SUCCESS,
  payload: {
    data
  }
});

const fetchPaymentTokenFailure = error => ({
  type: ActionType.FETCH_PAYMENT_TOKEN_FAILURE,
  payload: {
    error
  }
});

export const fetchPaymentToken = () => async dispatch => {
  dispatch(fetchPaymentTokenRequested());
  const result = await api.getPaymentTokens({});

  if (result.status === 'error') {
    dispatch(fetchPaymentTokenFailure(result.error));
    return undefined;
  }

  dispatch(fetchPaymentTokenSuccess({
    data: result.body.data
  }));
  return result.body.data;
};
export const invalidateStopSellingItem = () => ({
  type: ActionType.INVALIDATE_STOP_SELLING_ITEM,
  payload: undefined
});

const stopSellingItemRequested = () => ({
  type: ActionType.STOP_SELLING_ITEM_REQUESTED,
  payload: undefined
});

const stopSellingtemSuccess = () => ({
  type: ActionType.STOP_SELLING_ITEM_SUCCESS,
  payload: undefined
});

const stopSellingItemFailure = error => ({
  type: ActionType.STOP_SELLING_ITEM_FAILURE,
  payload: {
    error
  }
});

export const stopSellingItem = ({
  blockchainId
}) => async (dispatch, getState) => {
  dispatch(invalidateStopSellingItem());
  dispatch(stopSellingItemRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(stopSellingItemFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    data,
    error: stopSellingError
  } = await ethereum.cancelSellingItem({
    senderAddress: accounts[0],
    blockchainId
  });

  if (stopSellingError !== EipError.NO_ERROR) {
    dispatch(stopSellingItemFailure(stopSellingError));
    return {
      error: stopSellingError
    };
  }

  dispatch(stopSellingtemSuccess());
  return {
    data: {
      id: data
    }
  };
};

const fetchUserThetanBoxDetailRequested = boxType => ({
  type: ActionType.FETCH_THETAN_BOX_DETAIL_REQUESTED,
  payload: {
    boxType
  }
});

const fetchUserThetanBoxDetailSuccess = (amount, boxType, boxConfig, skinConfigs) => ({
  type: ActionType.FETCH_THETAN_BOX_DETAIL_SUCCESS,
  payload: {
    boxType,
    amount,
    boxConfig,
    skinConfigs
  }
});

const fetchUserThetanBoxDetailFailure = error => ({
  type: ActionType.FETCH_THETAN_BOX_DETAIL_FAILURE,
  payload: {
    error
  }
});

export const fetchUserThetanBoxDetail = boxType => async (dispatch, getState) => {
  dispatch(fetchUserThetanBoxDetailRequested(boxType));
  const response = await api.getUserThetanBoxDetail({
    configVer: getState().entities.configVer,
    boxType
  });

  if (response.status === 'error') {
    dispatch(fetchUserThetanBoxDetailFailure(response.error));
    return undefined;
  }

  const {
    amount,
    boxConfig,
    skinConfigs,
    boxConfigVer,
    boxConfigs
  } = response.body;
  dispatch(fetchUserThetanBoxDetailSuccess(amount, boxType, boxConfig, skinConfigs));
  if (boxConfigs) dispatch(pushBoxConfigs(boxConfigs));
  if (boxConfigVer) dispatch(saveConfigVerToEntities(boxConfigVer));

  if (skinConfigs) {
    const entities = skinConfigs.reduce((prev, e) => ({ ...prev,
      [e.id.toString()]: e
    }), {});
    dispatch(pushSkinConfigToEntities(entities));
  }

  return response.body;
};

const fetchUserGiftBoxDetailRequested = () => ({
  type: ActionType.FETCH_USER_GIFT_BOX_DETAIL_REQUESTED,
  payload: undefined
});

const fetchUserGiftBoxDetailSuccess = (amount, isInstalledGame, openTime) => ({
  type: ActionType.FETCH_USER_GIFT_BOX_DETAIL_SUCCESS,
  payload: {
    amount,
    isInstalledGame,
    openTime
  }
});

const fetchUserGiftBoxDetailFailure = error => ({
  type: ActionType.FETCH_USER_GIFT_BOX_DETAIL_FAILURE,
  payload: {
    error
  }
});

export const fetchUserGiftBoxDetail = boxName => async (dispatch, getState) => {
  dispatch(fetchUserGiftBoxDetailRequested());
  const response = await api.getUserGiftBoxDetail({
    configVer: getState().entities.configVer,
    boxTypeStr: boxName
  });

  if (response.status === 'error') {
    dispatch(fetchUserGiftBoxDetailFailure(response.error));
    return undefined;
  }

  const {
    amount,
    isInstalledGame,
    openTime
  } = response.body;
  dispatch(fetchUserGiftBoxDetailSuccess(amount, isInstalledGame, openTime));
  return response.body;
};

const getThetanBoxDashboardRequested = () => ({
  type: ActionType.FETCH_THETAN_BOX_DASHBOARD_REQUESTED,
  payload: undefined
});

const getThetanBoxDashboardSuccess = data => ({
  type: ActionType.FETCH_THETAN_BOX_DASHBOARD_SUCCESS,
  payload: {
    data
  }
});

const getThetanBoxDashboardFailure = error => ({
  type: ActionType.FETCH_THETAN_BOX_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

export const getThetanBoxDashboard = () => async dispatch => {
  dispatch(getThetanBoxDashboardRequested());
  const response = await api.getThetanBoxDashboard({});

  if (response.status === 'error') {
    dispatch(getThetanBoxDashboardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(getThetanBoxDashboardSuccess(response.body));
  return {
    data: response.body
  };
};

const fetchGiftBoxDashboardRequested = () => ({
  type: ActionType.FETCH_GIFT_BOX_DASHBOARD_REQUESTED,
  payload: undefined
});

const fetchGiftBoxDashboardSuccess = data => ({
  type: ActionType.FETCH_GIFT_BOX_DASHBOARD_SUCCESS,
  payload: {
    data
  }
});

const fetchGiftBoxDashboardFailure = error => ({
  type: ActionType.FETCH_GIFT_BOX_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

export const fetchGiftBoxDashboard = giftCode => async dispatch => {
  dispatch(fetchGiftBoxDashboardRequested());
  const code = giftCode ? giftCode : getGiftCode();
  const response = await api.getGiftBoxDashboard({
    giftCode: code
  });

  if (response.status === 'error') {
    dispatch(fetchGiftBoxDashboardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchGiftBoxDashboardSuccess(response.body));
  return {
    data: response.body
  };
};

const getBoxConfigRequested = () => ({
  type: ActionType.FETCH_BOX_CONFIG_REQUESTED,
  payload: undefined
});

const getBoxConfigSuccess = data => ({
  type: ActionType.FETCH_BOX_CONFIG_SUCCESS,
  payload: {
    data
  }
});

const getBoxConfigFailure = error => ({
  type: ActionType.FETCH_BOX_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const getBoxConfig = () => async (dispatch, getState) => {
  dispatch(getBoxConfigRequested());
  const response = await api.getBoxConfig({
    mkp: 2,
    configVer: getState().boxConfigsV2.configVer
  });

  if (response.status === 'error') {
    dispatch(getBoxConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  if (response.status === 'ok') {
    const {
      boxConfigs,
      configVer
    } = response.body;

    if (!!configVer && !!boxConfigs) {
      dispatch(getBoxConfigSuccess(response.body));
      return {
        data: response.body
      };
    }
  }

  return {
    error: AppError.UNNKOWN
  };
};

const getThetanBoxDashboardDetailRequested = boxTypeStr => ({
  type: ActionType.FETCH_THETAN_BOX_DASHBOARD_DETAIL_REQUESTED,
  payload: {
    boxTypeStr
  }
});

const getThetanBoxDashboardDetailSuccess = ({
  boxType,
  boxTypeStr,
  paymentToken,
  boxConfig,
  skinConfigs,
  boxInfo
}) => ({
  type: ActionType.FETCH_THETAN_BOX_DASHBOARD_DETAIL_SUCCESS,
  payload: {
    boxType,
    boxTypeStr,
    boxConfig,
    skinConfigs,
    paymentToken,
    boxInfo
  }
});

const getThetanBoxDashboardDetailFailure = error => ({
  type: ActionType.FETCH_THETAN_BOX_DASHBOARD_DETAIL_FAILURE,
  payload: {
    error
  }
});

export const getThetanBoxDashboardDetail = boxTypeStr => async (dispatch, getState) => {
  dispatch(getThetanBoxDashboardDetailRequested(boxTypeStr));
  const response = await api.getThetanBoxDashboardDetail({
    configVer: getState().entities.configVer,
    boxTypeStr
  });

  if (response.status === 'error') {
    dispatch(getThetanBoxDashboardDetailFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    boxConfig,
    skinConfigs,
    boxInfo,
    paymentToken,
    boxConfigVer,
    boxConfigs
  } = response.body;
  if (boxConfigs) dispatch(pushBoxConfigs(boxConfigs));
  if (boxConfigVer) dispatch(saveConfigVerToEntities(boxConfigVer));

  if (skinConfigs) {
    const entities = skinConfigs.reduce((prev, e) => ({ ...prev,
      [e.id.toString()]: e
    }), {});
    dispatch(pushSkinConfigToEntities(entities));
  }

  dispatch(getThetanBoxDashboardDetailSuccess({
    boxType: boxInfo.boxType,
    boxTypeStr,
    boxConfig,
    skinConfigs,
    boxInfo,
    paymentToken
  }));
  return {
    data: {
      boxType: boxInfo.boxType,
      boxTypeStr,
      boxConfig,
      skinConfigs,
      paymentToken,
      boxInfo
    }
  };
};

const fetchGiftBoxDashboardDetailRequested = boxType => ({
  type: ActionType.FETCH_GIFT_BOX_DASHBOARD_DETAIL_REQUESTED,
  payload: {
    boxType
  }
});

const fetchGiftBoxDashboardDetailSuccess = ({
  boxType,
  boxInfo
}) => ({
  type: ActionType.FETCH_GIFT_BOX_DASHBOARD_DETAIL_SUCCESS,
  payload: {
    boxType,
    boxInfo
  }
});

const fetchGiftBoxDashboardDetailFailure = error => ({
  type: ActionType.FETCH_GIFT_BOX_DASHBOARD_DETAIL_FAILURE,
  payload: {
    error
  }
});

export const fetchGiftBoxDashboardDetail = boxType => async (dispatch, getState) => {
  dispatch(fetchGiftBoxDashboardDetailRequested(boxType));
  const giftCode = getGiftCode();
  const response = await api.getGiftBoxDashboardDetail({
    configVer: getState().entities.configVer,
    boxTypeStr: boxType,
    giftCode
  });

  if (response.status === 'error') {
    dispatch(fetchGiftBoxDashboardDetailFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    boxInfo
  } = response.body;
  dispatch(fetchGiftBoxDashboardDetailSuccess({
    boxType,
    boxInfo
  }));
  return {
    data: {
      boxType,
      boxInfo
    }
  };
};
export const invalidateCheckoutBox = () => ({
  type: ActionType.INVALIDATE_CHECKOUT_BOX,
  payload: undefined
});

const checkoutBoxRequested = () => ({
  type: ActionType.CHECKOUT_BOX_REQUESTED,
  payload: undefined
});

const checkoutBoxSuccess = id => ({
  type: ActionType.CHECKOUT_BOX_SUCCESS,
  payload: {
    id
  }
});

const checkoutBoxFailure = error => ({
  type: ActionType.CHECKOUT_BOX_FAILURE,
  payload: {
    error
  }
});

export const prepareCheckoutBox = params => async dispatch => {
  const {
    amount,
    boxType,
    systemPrice,
    isGiftBox,
    gloryToken
  } = params;
  dispatch(invalidateCheckoutBox());
  dispatch(checkoutBoxRequested());
  const paymentTokenContract = contractAddress[systemPrice.token];

  if (!paymentTokenContract) {
    dispatch(checkoutBoxFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN,
      params
    };
  }

  const giftCode = isGiftBox ? getGiftCode() : undefined;
  const result = await api.postBuyThetanBox({
    boxType,
    amount,
    paymentTokenContract,
    price: systemCurrencyToServerCurrency(systemPrice),
    giftCode,
    gloryToken
  });

  if (result.status === 'error') {
    dispatch(checkoutBoxFailure(result.error));
    return {
      error: result.error,
      params
    };
  }

  return {
    data: result.body,
    params
  };
};
export const checkoutBox = args => async (dispatch, getState) => {
  const {
    params,
    data,
    error
  } = args;

  if (error || data === undefined) {
    const _error = error || AppError.UNNKOWN;

    checkoutBoxFailure(_error);
    return {
      error: _error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(checkoutBoxFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN,
      params
    };
  }

  const {
    id,
    boxId,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse
  } = data;
  const {
    error: confirmError
  } = params.isGiftBox ? await ethereum.claimGiftBox({
    id,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse,
    accountAddress: accounts[0]
  }) : await ethereum.buyThetanBox({
    id,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse,
    accountAddress: accounts[0]
  });

  if (confirmError !== EipError.NO_ERROR) {
    dispatch(checkoutBoxFailure(confirmError));
    return {
      error: confirmError
    };
  }

  dispatch(checkoutBoxSuccess(boxId));
  return {
    data: {
      id: boxId
    }
  };
};

const confirmStakeRequested = () => ({
  type: ActionType.CONFIRM_STAKE_REQUESTED,
  payload: undefined
});

const confirmStakeSuccess = data => ({
  type: ActionType.CONFIRM_STAKE_SUCCESS,
  payload: {
    data
  }
});

const confirmStakeFailure = error => ({
  type: ActionType.CONFIRM_STAKE_FAILURE,
  payload: {
    error
  }
});

export const prepareConfirmStake = params => async dispatch => {
  dispatch(confirmStakeRequested());
  const {
    plan,
    systemPrice,
    tokenIds,
    desc,
    isSelectAllTokens,
    sort
  } = params;
  const result = await api.confirmStake({
    plan,
    tokenIds,
    amount: systemCurrencyToServerCurrency(systemPrice),
    isSelectAllTokens,
    sort,
    desc
  });

  if (result.status === 'error') {
    dispatch(confirmStakeFailure(result.error));
    return {
      error: result.error,
      params
    };
  }

  return {
    data: result.body,
    params
  };
};
export const confirmStake = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError,
    params
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    confirmStakeFailure(_error);
    return {
      error: _error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(confirmStakeFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    blockchainId = 0,
    signature: signatureResponse = '',
    expiredAt = 0,
    lockedDuration = 0,
    tokenIds: tokenIdsResponse = []
  } = preparedData;
  const {
    error: confirmError
  } = await ethereum.confirmStake({
    id: blockchainId,
    amount: systemCurrencyToBlockchainNumber(params.systemPrice),
    lockedDuration,
    expiredAt,
    signature: signatureResponse,
    tokenIds: tokenIdsResponse,
    accountAddress: accounts[0]
  });

  if (confirmError !== EipError.NO_ERROR) {
    dispatch(confirmStakeFailure(confirmError));
    return {
      error: confirmError
    };
  }

  dispatch(confirmStakeSuccess(preparedData));
  return {
    data: preparedData,
    error: AppError.NO_ERROR
  };
};

const fetchMyStakingRequested = () => ({
  type: ActionType.FETCH_MY_STAKING_REQUESTED,
  payload: undefined
});

const fetchMyStakingSuccess = (data, summary) => ({
  type: ActionType.FETCH_MY_STAKING_SUCCESS,
  payload: {
    data,
    summary
  }
}); // eslint-disable-next-line @typescript-eslint/no-unused-vars


const fetchMyStakingFailure = error => ({
  type: ActionType.FETCH_MY_STAKING_FAILURE,
  payload: {
    error
  }
});

export const fetchMyStaking = () => async dispatch => {
  dispatch(fetchMyStakingRequested());
  const response = await api.getMyStaking({
    isUnlimited: true,
    withSummary: true
  });

  if (response.status === 'error') {
    dispatch(fetchMyStakingFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchMyStakingSuccess(response.body.data, response.body.summary));
  return {
    data: response.body.data,
    summary: response.body.summary
  };
};

const getSpecialEventConfigRequested = () => ({
  type: ActionType.FETCH_SPECIAL_EVENT_CONFIG_REQUESTED,
  payload: undefined
});

const getSpecialEventConfigSuccess = data => ({
  type: ActionType.FETCH_SPECIAL_EVENT_CONFIG_SUCCESS,
  payload: {
    data
  }
});

const getSpecialEventConfigFailure = error => ({
  type: ActionType.FETCH_SPECIAL_EVENT_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const getSpecialEventConfig = () => async dispatch => {
  dispatch(getSpecialEventConfigRequested());
  const response = await api.getSpecialEventConfig({});

  if (response.status === 'error') {
    dispatch(getSpecialEventConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(getSpecialEventConfigSuccess(response.body));
  return {
    data: response.body
  };
};

const getSpecialEventLeaderboardRequested = () => ({
  type: ActionType.FETCH_SPECIAL_EVENT_LEADERBOARD_REQUESTED,
  payload: undefined
});

const getSpecialEventLeaderboardSuccess = data => ({
  type: ActionType.FETCH_SPECIAL_EVENT_LEADERBOARD_SUCCESS,
  payload: {
    data
  }
});

const getSpecialEventLeaderboardFailure = error => ({
  type: ActionType.FETCH_SPECIAL_EVENT_LEADERBOARD_FAILURE,
  payload: {
    error
  }
});

export const getSpecialEventLeaderboard = (lbType, pageNumber, pageSize) => async dispatch => {
  dispatch(getSpecialEventLeaderboardRequested());
  const response = await api.getSpecialEventLeaderboard({
    lbType,
    pageNumber,
    pageSize
  });

  if (response.status === 'error') {
    dispatch(getSpecialEventLeaderboardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(getSpecialEventLeaderboardSuccess(response.body));
  return {
    data: response.body
  };
};
export const pushSkinConfigToEntities = skinConfig => async dispatch => {
  dispatch({
    type: ActionType.ADD_SKIN_CONFIG_ENTITIES,
    payload: {
      skinConfig
    }
  });
  return;
};
export const pushBoxConfigs = boxConfigs => ({
  type: ActionType.SAVE_BOX_CONFIGS,
  payload: {
    boxConfigs
  }
});
export const saveSkinVerToEntities = skinVer => ({
  type: ActionType.SAVE_SKIN_VER,
  payload: {
    skinVer
  }
});
export const saveConfigVerToEntities = configVer => ({
  type: ActionType.SAVE_CONFIG_VER,
  payload: {
    configVer
  }
});

const fetchListNotificationRequested = () => ({
  type: ActionType.FETCH_LIST_NOTIFICATION_REQUESTED,
  payload: undefined
});

const fetchListNotificationSuccess = data => ({
  type: ActionType.FETCH_LIST_NOTIFICATION_SUCCESS,
  payload: {
    data
  }
});

const fetchListNotificationFailure = error => ({
  type: ActionType.FETCH_LIST_NOTIFICATION_FAILURE,
  payload: {
    error
  }
});

export const fetchListNotification = () => async dispatch => {
  dispatch(fetchListNotificationRequested());
  const result = await api.getListNotification({});

  if (result.status === 'error') {
    dispatch(fetchListNotificationFailure(result.error));
    return {
      error: result.error
    };
  }

  dispatch(fetchListNotificationSuccess(result.body));
  return {
    data: result.body
  };
};

const invalidateCallout = tab => ({
  type: ActionType.INVALIDATED_CALLOUT,
  payload: {
    tab
  }
});

export const updateNotification = tab => async dispatch => {
  dispatch(invalidateCallout(tab));
  const result = await api.updateNotification({
    tab
  });

  if (result.status === 'error') {
    return {
      error: result.error
    };
  }

  return {
    data: result.body
  };
};
export const searchClaimBoxes = (tokenId, sort, page, size) => async dispatch => {
  dispatch(searchClaimBoxesRequested());
  const response = await api.searchClaimBoxes({
    tokenId,
    from: (page - 1) * size,
    size,
    s: sort
  });

  if (response.status === 'error') {
    dispatch(searchClaimBoxesFailure(response.error));
    return;
  }

  const {
    totalItems,
    data
  } = response.body;
  dispatch(searchClaimBoxesSuccess(data, totalItems));
  return {
    totalItems,
    data
  };
};

const searchClaimBoxesRequested = () => ({
  type: ActionType.FETCH_CLAIM_BOXES_REQUESTED,
  payload: undefined
});

const searchClaimBoxesSuccess = (data, totalItems) => ({
  type: ActionType.FETCH_CLAIM_BOXES_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

const searchClaimBoxesFailure = error => ({
  type: ActionType.FETCH_CLAIM_BOXES_FAILURE,
  payload: {
    error
  }
});

export const claimBox = tokenId => async () => {
  const result = await api.claimBox({
    tokenId
  });

  if (result.status === 'error') {
    return {
      error: result.error
    };
  }

  return {
    data: result.body
  };
};
export const fetchBoxPrice = boxType => async () => {
  const response = await api.getBoxPrice({
    boxType
  });

  if (response.status === 'error') {
    return {
      error: response.error
    };
  }

  return {
    price: response.body.price
  };
};

const updateSearchCosmeticParams = searchCosmeticParams => ({
  type: ActionType.UPDATE_SEARCH_COSMETIC_PARAMS,
  payload: {
    searchCosmeticParams
  }
});

export const searchCosmetic = searchCosmeticParams => async dispatch => {
  const params = { ...searchCosmeticParams,
    size: searchCosmeticParams.size || ITEM_PER_PAGE
  };
  dispatch(updateSearchCosmeticParams(params));
  return await dispatch(fetchCosmeticResult(params));
};

const fetchCosmeticResultRequested = searchCosmeticParams => ({
  type: ActionType.FETCH_SEARCH_COSMETIC_LIST_REQUESTED,
  payload: {
    searchCosmeticParams
  }
});

const fetchCosmeticResultSuccess = (searchCosmeticParams, data, totalItems) => ({
  type: ActionType.FETCH_SEARCH_COSMETIC_LIST_SUCCESS,
  payload: {
    searchCosmeticParams,
    data,
    totalItems
  }
});

const fetchCosmeticResultFailure = (searchCosmeticParams, error) => ({
  type: ActionType.FETCH_SEARCH_COSMETIC_LIST_FAILURE,
  payload: {
    searchCosmeticParams,
    error
  }
});

export const fetchCosmeticResult = searchCosmeticParams => async dispatch => {
  dispatch(fetchCosmeticResultRequested(searchCosmeticParams));
  const response = await api.searchCosmeticList({ ...searchCosmeticParams,
    mkpRefType: MKPRefType.COSMETIC
  });

  if (response.status === 'error') {
    dispatch(fetchCosmeticResultFailure(searchCosmeticParams, response.error));
    return undefined;
  }

  const {
    totalItems,
    data
  } = response.body;
  dispatch(fetchCosmeticResultSuccess(searchCosmeticParams, data, totalItems));
  return data;
};

const fetchCosmeticDetailRequested = () => ({
  type: ActionType.FETCH_COSMETIC_DETAIL_REQUESTED,
  payload: undefined
});

const fetchCosmeticDetailFailured = error => ({
  type: ActionType.FETCH_COSMETIC_DETAIL_FAILURE,
  payload: {
    error
  }
});

const fetchCosmeticDetailSuccess = data => ({
  type: ActionType.FETCH_COSMETIC_DETAIL_SUCCESS,
  payload: {
    data
  }
});

export const fetchCosmeticDetail = id => async dispatch => {
  dispatch(fetchCosmeticDetailRequested());
  const response = await api.getCosmeticDetail({
    id
  });

  if (response.status === 'error') {
    dispatch(fetchCosmeticDetailFailured(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchCosmeticDetailSuccess(data));
  return {
    data
  };
};
export const invalidateSellingCosmetic = () => ({
  type: ActionType.INVALIDATE_SELLING_COSMETIC,
  payload: undefined
});

const sellingCosmeticRequested = () => ({
  type: ActionType.SELLING_COSMETIC_REQUESTED,
  payload: undefined
});

const sellingCosmeticSuccess = () => ({
  type: ActionType.SELLING_COSMETIC_SUCCESS,
  payload: undefined
});

const sellingCosmeticFailure = error => ({
  type: ActionType.SELLING_COSMETIC_FAILURE,
  payload: {
    error
  }
});

export const prepareSellingCosmetic = params => async dispatch => {
  dispatch(invalidateSellingCosmetic());
  dispatch(sellingCosmeticRequested());
  const {
    systemCurrency,
    tokenId
  } = params;
  const signSignatureRes = await api.signSellingSignatureCosmetic({
    nftAddress: COSMETIC_NFT_CONTRACT_ADDRESS,
    paymentTokenAddress: THETAN_COIN_CONTRACT_ADDRESS,
    tokenId,
    price: systemCurrencyToServerCurrency(systemCurrency)
  });

  if (signSignatureRes.status === 'error') {
    dispatch(sellingCosmeticFailure(signSignatureRes.error));
    return {
      error: signSignatureRes.error,
      params
    };
  }

  return {
    data: signSignatureRes.body,
    params
  };
};
export const sellingCosmetic = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    sellingCosmeticFailure(_error);
    return {
      error: _error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(sellingCosmeticFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    blockchainId,
    expiredAt,
    nftAddress,
    paymentTokenAddress,
    price,
    signature,
    tokenId: _tokenId
  } = preparedData;
  const {
    data,
    error
  } = await ethereum.sellItem({
    senderAddress: accounts[0],
    blockchainId,
    expiredAt,
    nftAddress,
    paymentTokenAddress,
    systemPrice: price,
    signature,
    tokenId: _tokenId
  });

  if (error !== EipError.NO_ERROR) {
    dispatch(sellingCosmeticFailure(error));
    return {
      error: error
    };
  }

  dispatch(sellingCosmeticSuccess());
  return {
    data: {
      id: data
    }
  };
};
export const invalidateStopSellingCosmetic = () => ({
  type: ActionType.INVALIDATE_STOP_SELLING_COSMETIC,
  payload: undefined
});

const stopSellingCosmeticRequested = () => ({
  type: ActionType.STOP_SELLING_COSMETIC_REQUESTED,
  payload: undefined
});

const stopSellingCosmeticSuccess = () => ({
  type: ActionType.STOP_SELLING_COSMETIC_SUCCESS,
  payload: undefined
});

const stopSellingCosmeticFailure = error => ({
  type: ActionType.STOP_SELLING_COSMETIC_FAILURE,
  payload: {
    error
  }
});

export const stopSellingCosmetic = ({
  blockchainId
}) => async (dispatch, getState) => {
  dispatch(invalidateStopSellingCosmetic());
  dispatch(stopSellingCosmeticRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(stopSellingCosmeticFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    data,
    error: stopSellingError
  } = await ethereum.cancelSellingItem({
    senderAddress: accounts[0],
    blockchainId
  });

  if (stopSellingError !== EipError.NO_ERROR) {
    dispatch(stopSellingCosmeticFailure(stopSellingError));
    return {
      error: stopSellingError
    };
  }

  dispatch(stopSellingCosmeticSuccess());
  return {
    data: {
      id: data
    }
  };
};
export const invalidatePurchaseCosmetic = () => ({
  type: ActionType.INVALIDATE_PURCHASE_COSMETIC,
  payload: undefined
});

const purchaseCosmeticRequested = () => ({
  type: ActionType.PURCHASE_COSMETIC_REQUESTED,
  payload: undefined
});

const purchaseCosmeticSuccess = () => ({
  type: ActionType.PURCHASE_COSMETIC_SUCCESS,
  payload: undefined
});

const purchaseCosmeticFailure = error => ({
  type: ActionType.PURCHASE_COSMETIC_FAILURE,
  payload: {
    error
  }
});

export const purchaseCosmetic = ({
  systemPrice,
  blockchainId
}) => async (dispatch, getState) => {
  dispatch(invalidatePurchaseCosmetic());
  dispatch(purchaseCosmeticRequested());
  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(purchaseCosmeticFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    error: checkoutError,
    data
  } = await ethereum.checkoutItem({
    price: systemPrice,
    senderAddress: accounts[0],
    blockchainId,
    paymentTokenAddress: THETAN_COIN_CONTRACT_ADDRESS
  });

  if (checkoutError !== EipError.NO_ERROR) {
    dispatch(purchaseCosmeticFailure(checkoutError));
    return {
      error: checkoutError
    };
  }

  if (!data) {
    dispatch(purchaseCosmeticFailure(AppError.UNNKOWN));
    return {
      error: AppError.UNNKOWN
    };
  }

  dispatch(purchaseCosmeticSuccess());
  return {
    data: {
      id: data
    }
  };
};

const fetchCosmeticSaleHistoryRequested = () => ({
  type: ActionType.FETCH_COSMETIC_SALE_HISTORY_REQUESTED,
  payload: undefined
});

const fetchCosmeticSaleHistoryFailure = error => ({
  type: ActionType.FETCH_COSMETIC_SALE_HISTORY_FAILURE,
  payload: {
    error
  }
});

const fetchCosmeticSaleHistorySuccess = (data, totalItems) => ({
  type: ActionType.FETCH_COSMETIC_SALE_HISTORY_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

export const fetchCosmeticSaleHistory = (id, from, size) => async dispatch => {
  dispatch(fetchCosmeticSaleHistoryRequested());
  const response = await api.getCosmeticSaleHistory({
    id,
    from,
    size
  });

  if (response.status === 'error') {
    dispatch(fetchCosmeticSaleHistoryFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data,
    totalItems
  } = response.body;
  dispatch(fetchCosmeticSaleHistorySuccess(data, totalItems));
  return {
    data,
    totalItems
  };
};

const fetchSpecialEventRewardsRequested = () => ({
  type: ActionType.FETCH_SPECIAL_EVENT_REWARDS_REQUESTED,
  payload: undefined
});

const fetchSpecialEventRewardsFailure = error => ({
  type: ActionType.FETCH_SPECIAL_EVENT_REWARDS_FAILURE,
  payload: {
    error
  }
});

const fetchSpecialEventRewardsSuccess = data => ({
  type: ActionType.FETCH_SPECIAL_EVENT_REWARDS_SUCCESS,
  payload: {
    data
  }
});

export const fetchSpecialEventRewards = ({
  isDaily,
  eventType
}) => async dispatch => {
  dispatch(fetchSpecialEventRewardsRequested());
  const response = await getSpecialEventRewards({
    isDaily,
    eventType
  });

  if (response.status === 'error') {
    dispatch(fetchSpecialEventRewardsFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchSpecialEventRewardsSuccess(data));
  return {
    data
  };
};

const fetchUserRankingRequested = () => ({
  type: ActionType.FETCH_USER_RANKING_REQUESTED,
  payload: undefined
});

const fetchUserRankingFailure = error => ({
  type: ActionType.FETCH_USER_RANKING_FAILURE,
  payload: {
    error
  }
});

const fetchUserRankingSuccess = data => ({
  type: ActionType.FETCH_USER_RANKING_SUCCESS,
  payload: {
    data
  }
});

export const fetchUserRanking = () => async dispatch => {
  dispatch(fetchUserRankingRequested());
  const response = await getUserRanking({});

  if (response.status === 'error') {
    dispatch(fetchUserRankingFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchUserRankingSuccess(data));
  return {
    data
  };
};

const fetchTradingEventTurnRequested = () => ({
  type: ActionType.FETCH_TRADING_EVENT_TURN_REQUESTED,
  payload: undefined
});

const fetchTradingEventTurnFailure = error => ({
  type: ActionType.FETCH_TRADING_EVENT_TURN_FAILURE,
  payload: {
    error
  }
});

const fetchTradingEventTurnSuccess = data => ({
  type: ActionType.FETCH_TRADING_EVENT_TURN_SUCCESS,
  payload: {
    data
  }
});

export const fetchTradingEventTurn = seasonId => async dispatch => {
  dispatch(fetchTradingEventTurnRequested());
  const response = await api.getTradingEventTurn({
    seasonId
  });

  if (response.status === 'error') {
    dispatch(fetchTradingEventTurnFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchTradingEventTurnSuccess(data));
  return {
    data
  };
};

const fetchJoinSpecialEventRequested = () => ({
  type: ActionType.FETCH_JOIN_SPECIAL_EVENT_REQUESTED,
  payload: undefined
});

const fetchJoinSpecialEventFailure = error => ({
  type: ActionType.FETCH_JOIN_SPECIAL_EVENT_FAILURE,
  payload: {
    error
  }
});

const fetchJoinSpecialEventSuccess = data => ({
  type: ActionType.FETCH_JOIN_SPECIAL_EVENT_SUCCESS,
  payload: {
    data
  }
});

export const fetchJoinSpecialEvent = eventType => async dispatch => {
  dispatch(fetchJoinSpecialEventRequested());
  const response = await postJoinSpecialEvent({
    eventType
  });

  if (response.status === 'error') {
    dispatch(fetchJoinSpecialEventFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchJoinSpecialEventSuccess(data));
  return {
    data
  };
};

const fetchProjectListRequested = () => ({
  type: ActionType.FETCH_PROJECT_LIST_REQUESTED,
  payload: undefined
});

const fetchProjectListFailure = error => ({
  type: ActionType.FETCH_PROJECT_LIST_FAILURE,
  payload: {
    error
  }
});

const fetchProjectListSuccess = data => ({
  type: ActionType.FETCH_PROJECT_LIST_SUCCESS,
  payload: {
    data
  }
});

export const fetchProjectList = ({
  cursor,
  limit,
  projectStatus,
  allowlistStatus
}) => async dispatch => {
  dispatch(fetchProjectListRequested());
  let response;

  if (projectStatus !== 'my-list') {
    response = await api.getProjectList({
      cursor,
      limit,
      projectStatus,
      allowlistStatus
    });
  } else {
    response = await api.getMyProjectList({
      cursor,
      limit
    });
  }

  if (response.status === 'error') {
    dispatch(fetchProjectListFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchProjectListSuccess(data));
  return {
    data
  };
};

const fetchFusionCostRequested = () => ({
  type: ActionType.FETCH_FUSION_COST_REQUESTED,
  payload: undefined
});

const fetchFusionCostFailure = error => ({
  type: ActionType.FETCH_FUSION_COST_FAILURE,
  payload: {
    error
  }
});

const fetchFusionCostSuccess = data => ({
  type: ActionType.FETCH_FUSION_COST_SUCCESS,
  payload: {
    data
  }
});

export const getFusionCost = ({
  heroIds
}) => async dispatch => {
  dispatch(fetchFusionCostRequested());
  const result = await api.getFusionCost({
    matIds: heroIds
  });

  if (result.status === 'error') {
    dispatch(fetchFusionCostFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data
  } = result.body;
  dispatch(fetchFusionCostSuccess(data));
  return {
    data
  };
};

const fetchHomeBannerRequested = () => ({
  type: ActionType.FETCH_HOME_BANNER_REQUESTED,
  payload: undefined
});

const fetchHomeBannerFailure = error => ({
  type: ActionType.FETCH_HOME_BANNER_FAILURE,
  payload: {
    error
  }
});

const fetchHomeBannerSuccess = data => ({
  type: ActionType.FETCH_HOME_BANNER_SUCCESS,
  payload: {
    data
  }
});

export const fetchHomeBanner = () => async dispatch => {
  dispatch(fetchHomeBannerRequested());
  const result = await api.fetchHomeBanner({});

  if (result.status === 'error') {
    dispatch(fetchHomeBannerFailure(result.error));
    return undefined;
  }

  dispatch(fetchHomeBannerSuccess(result.body));
  return result.body;
};

const fetchMetricStatDashboardRequested = () => ({
  type: ActionType.FETCH_METRIC_STATS_DASHBOARD_REQUESTED,
  payload: undefined
});

const fetchMetricStatDashboardFailure = error => ({
  type: ActionType.FETCH_METRIC_STATS_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

const fetchMetricStatDashboardSuccess = data => ({
  type: ActionType.FETCH_METRIC_STATS_DASHBOARD_SUCCESS,
  payload: {
    data
  }
});

export const fetchMetricStatDashboard = groupCount => async dispatch => {
  dispatch(fetchMetricStatDashboardRequested());
  const result = await api.fetchMetricStatDashboard({
    groupCount
  });

  if (result.status === 'error') {
    dispatch(fetchMetricStatDashboardFailure(result.error));
    return undefined;
  }

  dispatch(fetchMetricStatDashboardSuccess(result.body));
  return result.body;
};

const fetchHeroesListedRequested = () => ({
  type: ActionType.FETCH_HEROES_LISTED_DASHBOARD_REQUESTED,
  payload: undefined
});

const fetchHeroesListedFailure = error => ({
  type: ActionType.FETCH_HEROES_LISTED_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

const fetchHeroesListedSuccess = data => ({
  type: ActionType.FETCH_HEROES_LISTED_DASHBOARD_SUCCESS,
  payload: {
    data
  }
});

export const fetchHeroesListed = size => async dispatch => {
  dispatch(fetchHeroesListedRequested());
  const result = await api.fetchHeroesListed({
    size
  });

  if (result.status === 'error') {
    dispatch(fetchHeroesListedFailure(result.error));
    return undefined;
  }

  dispatch(fetchHeroesListedSuccess(result.body));
  return result.body;
};

const fetchHeroesTradedRequested = () => ({
  type: ActionType.FETCH_HEROES_TRADED_DASHBOARD_REQUESTED,
  payload: undefined
});

const fetchHeroesTradedFailure = error => ({
  type: ActionType.FETCH_HEROES_TRADED_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

const fetchHeroesTradedSuccess = (data, totalItems) => ({
  type: ActionType.FETCH_HEROES_TRADED_DASHBOARD_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

export const fetchHeroesTraded = (size, from) => async dispatch => {
  dispatch(fetchHeroesTradedRequested());
  const result = await api.fetchHeroesTraded({
    size,
    from
  });

  if (result.status === 'error') {
    dispatch(fetchHeroesTradedFailure(result.error));
    return undefined;
  }

  dispatch(fetchHeroesTradedSuccess(result.body.data, result.body.totalItems));
  return result.body;
};

const fetchCosmeticListedRequested = () => ({
  type: ActionType.FETCH_COSMETIC_LISTED_DASHBOARD_REQUESTED,
  payload: undefined
});

const fetchCosmeticListedFailure = error => ({
  type: ActionType.FETCH_COSMETIC_LISTED_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

const fetchCosmeticListedSuccess = data => ({
  type: ActionType.FETCH_COSMETIC_LISTED_DASHBOARD_SUCCESS,
  payload: {
    data
  }
});

export const fetchCosmeticListed = size => async dispatch => {
  dispatch(fetchCosmeticListedRequested());
  const result = await api.fetchCosmeticListed({
    size
  });

  if (result.status === 'error') {
    dispatch(fetchCosmeticListedFailure(result.error));
    return undefined;
  }

  dispatch(fetchCosmeticListedSuccess(result.body));
  return result.body;
};

const fetchCosmeticTradedRequested = () => ({
  type: ActionType.FETCH_COSMETIC_TRADED_DASHBOARD_REQUESTED,
  payload: undefined
});

const fetchCosmeticTradedFailure = error => ({
  type: ActionType.FETCH_COSMETIC_TRADED_DASHBOARD_FAILURE,
  payload: {
    error
  }
});

const fetchCosmeticTradedSuccess = (data, totalItems) => ({
  type: ActionType.FETCH_COSMETIC_TRADED_DASHBOARD_SUCCESS,
  payload: {
    data,
    totalItems
  }
});

export const fetchCosmeticTraded = (size, from) => async dispatch => {
  dispatch(fetchCosmeticTradedRequested());
  const result = await api.fetchCosmeticTraded({
    size,
    from
  });

  if (result.status === 'error') {
    dispatch(fetchCosmeticTradedFailure(result.error));
    return undefined;
  }

  dispatch(fetchCosmeticTradedSuccess(result.body.data, result.body.totalItems));
  return result.body;
};

const refreshOutputFusionRequested = () => ({
  type: ActionType.REFRESH_OUTPUT_FUSION_REQUESTED,
  payload: undefined
});

const refreshOutputFusionFailure = error => ({
  type: ActionType.REFRESH_OUTPUT_FUSION_FAILURE,
  payload: {
    error
  }
});

const refreshOutputFusionSuccess = data => ({
  type: ActionType.REFRESH_OUTPUT_FUSION_SUCCESS,
  payload: {
    data
  }
});

export const refreshOutputFusion = () => async dispatch => {
  dispatch(refreshOutputFusionRequested());
  const result = await api.refreshOutputFusion({});

  if (result.status === 'error') {
    dispatch(refreshOutputFusionFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data
  } = result.body;
  dispatch(refreshOutputFusionSuccess(data));
  return {
    data
  };
};

const confirmFuseRequested = () => ({
  type: ActionType.CONFIRM_FUSE_HERO_REQUESTED,
  payload: undefined
});

const confirmFuseFailure = error => ({
  type: ActionType.CONFIRM_FUSE_HERO_FAILURE,
  payload: {
    error
  }
});

const confirmFuseSuccess = data => ({
  type: ActionType.CONFIRM_FUSE_HERO_SUCCESS,
  payload: {
    data
  }
});

export const prepareConfirmFuse = params => async dispatch => {
  dispatch(confirmFuseRequested());
  const {
    inputHeroIds,
    outputSkinId,
    cost
  } = params;
  const result = await api.confirmFuse({
    matIds: inputHeroIds,
    output: outputSkinId,
    fcost: systemCurrencyToServerCurrency(cost)
  });

  if (result.status === 'error') {
    dispatch(confirmFuseFailure(result.error));
    return {
      error: result.error,
      params
    };
  }

  return {
    data: result.body.data,
    params
  };
};
export const confirmFuse = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    confirmFuseFailure(_error);
    return {
      error: _error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(confirmFuseFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    amount,
    expiredAt,
    signature: signatureResponse,
    tokenIds,
    blockchainId
  } = preparedData;
  const {
    error: confirmError
  } = await ethereum.confirmFuse({
    id: blockchainId,
    amount,
    expiredAt,
    signature: signatureResponse,
    tokenIds,
    accountAddress: accounts[0]
  });

  if (confirmError !== EipError.NO_ERROR) {
    dispatch(confirmFuseFailure(confirmError));
    return {
      error: confirmError
    };
  }

  dispatch(confirmFuseSuccess(preparedData));
  return {
    data: preparedData,
    error: AppError.NO_ERROR
  };
};

const fetchFusionSeasonRequested = () => ({
  type: ActionType.FETCH_FUSION_SEASON_REQUESTED,
  payload: undefined
});

const fetchFusionSeasonFailure = error => ({
  type: ActionType.FETCH_FUSION_SEASON_FAILURE,
  payload: {
    error
  }
});

const fetchFusionSeasonSuccess = data => ({
  type: ActionType.FETCH_FUSION_SEASON_SUCCESS,
  payload: {
    data
  }
});

export const fetchFusionSeason = () => async dispatch => {
  dispatch(fetchFusionSeasonRequested());
  const response = await api.getFusionSeason({});

  if (response.status === 'error') {
    dispatch(fetchFusionSeasonFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchFusionSeasonSuccess(data));
  return {
    data
  };
};

const fetchPrevFusionRewardRequested = () => ({
  type: ActionType.FETCH_CLAIM_PREVIOUS_FUSION_REWARD_REQUESTED,
  payload: undefined
});

const fetchPrevFusionRewardFailure = error => ({
  type: ActionType.FETCH_CLAIM_PREVIOUS_FUSION_REWARD_FAILURE,
  payload: {
    error
  }
});

const fetchPrevFusionRewardSuccess = data => ({
  type: ActionType.FETCH_CLAIM_PREVIOUS_FUSION_REWARD_SUCCESS,
  payload: {
    data
  }
});

export const fetchPreviousFusionReward = () => async dispatch => {
  dispatch(fetchPrevFusionRewardRequested());
  const response = await api.postPrevFusionReward({});

  if (response.status === 'error') {
    dispatch(fetchPrevFusionRewardFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchPrevFusionRewardSuccess(data));
  return {
    data
  };
};

const fetchFusionTurnRequested = () => ({
  type: ActionType.FETCH_FUSION_TURN_REQUESTED,
  payload: undefined
});

const fetchFusionTurnFailure = error => ({
  type: ActionType.FETCH_FUSION_TURN_FAILURE,
  payload: {
    error
  }
});

const fetchFusionTurnSuccess = data => ({
  type: ActionType.FETCH_FUSION_TURN_SUCCESS,
  payload: {
    data
  }
});

export const fetchFusionTurn = () => async dispatch => {
  dispatch(fetchFusionTurnRequested());
  const result = await api.getFusionTurn({});

  if (result.status === 'error') {
    dispatch(fetchFusionTurnFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data
  } = result.body;
  dispatch(fetchFusionTurnSuccess(data));
  return {
    data
  };
};

const fetchBattleCapFusionRequested = () => ({
  type: ActionType.FETCH_BATTLE_CAP_FUSION_REQUESTED,
  payload: undefined
});

const fetchBattleCapFusionFailure = error => ({
  type: ActionType.FETCH_BATTLE_CAP_FUSION_FAILURE,
  payload: {
    error
  }
});

const fetchBattleCapFusionSuccess = (configVer, data) => ({
  type: ActionType.FETCH_BATTLE_CAP_FUSION_SUCCESS,
  payload: {
    data,
    configVer
  }
});

export const fetchBattleCapHeroFusion = () => async (dispatch, getState) => {
  dispatch(fetchBattleCapFusionRequested());
  const result = await api.getBattleCapHeroFusion({
    battleCapVer: getState().battleCapHeroFusion.configVer
  });

  if (result.status === 'error') {
    dispatch(fetchBattleCapFusionFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data,
    configVer
  } = result.body;
  dispatch(fetchBattleCapFusionSuccess(configVer, data));
  return {
    data
  };
};

const fetchClaimCashbackRequested = () => ({
  type: ActionType.FETCH_CLAIM_CASHBACK_REQUESTED,
  payload: undefined
});

const fetchClaimCashbackFailure = error => ({
  type: ActionType.FETCH_CLAIM_CASHBACK_FAILURE,
  payload: {
    error
  }
});

const fetchClaimCashbackSuccess = data => ({
  type: ActionType.FETCH_CLAIM_CASHBACK_SUCCESS,
  payload: {
    data
  }
});

export const fetchClaimCashback = id => async dispatch => {
  dispatch(fetchClaimCashbackRequested());
  const result = await api.postClaimCashback({
    id
  });

  if (result.status === 'error') {
    dispatch(fetchClaimCashbackFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data
  } = result.body;
  dispatch(fetchClaimCashbackSuccess(data));
  return {
    data
  };
};

const fetchFusionLeaderboardRequested = () => ({
  type: ActionType.FETCH_FUSION_LEADERBOARD_REQUESTED,
  payload: undefined
});

const fetchFusionLeaderboardSuccess = data => ({
  type: ActionType.FETCH_FUSION_LEADERBOARD_SUCCESS,
  payload: {
    data
  }
});

const fetchFusionLeaderboardFailure = error => ({
  type: ActionType.FETCH_FUSION_LEADERBOARD_FAILURE,
  payload: {
    error
  }
});

export const fetchFusionLeaderboard = (seasonId, page, size) => async dispatch => {
  dispatch(fetchFusionLeaderboardRequested());
  const response = await api.getFusionLeaderboard({
    seasonId,
    from: page,
    size
  });

  if (response.status === 'error') {
    dispatch(fetchFusionLeaderboardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchFusionLeaderboardSuccess(response.body));
  return {
    data: response.body
  };
};

const fetchSpecialEventListRequested = () => ({
  type: ActionType.FETCH_SPECIAL_EVENT_LIST_REQUESTED,
  payload: undefined
});

const fetchSpecialEventListFailure = error => ({
  type: ActionType.FETCH_SPECIAL_EVENT_LIST_FAILURE,
  payload: {
    error
  }
});

const fetchSpecialEventListSuccess = data => ({
  type: ActionType.FETCH_SPECIAL_EVENT_LIST_SUCCESS,
  payload: {
    data
  }
});

export const fetchSpecialEventList = () => async dispatch => {
  dispatch(fetchSpecialEventListRequested());
  const response = await api.getSpecialEventList({});

  if (response.status === 'error') {
    dispatch(fetchSpecialEventListFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchSpecialEventListSuccess(data));
  return {
    data
  };
};

const fetchQuestConfigRequested = () => ({
  type: ActionType.FETCH_QUEST_CONFIG_REQUESTED,
  payload: undefined
});

const fetchQuestConfigSuccess = (data, rewardConfig) => ({
  type: ActionType.FETCH_QUEST_CONFIG_SUCCESS,
  payload: {
    data,
    rewardConfig
  }
});

const fetchQuestConfigFailure = error => ({
  type: ActionType.FETCH_QUEST_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const fetchQuestConfig = () => async dispatch => {
  dispatch(fetchQuestConfigRequested());
  const response = await api.getQuestConfig({});

  if (response.status === 'error') {
    dispatch(fetchQuestConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchQuestConfigSuccess(response.body.data, response.body.rewardConfig));
  return {
    data: response.body.data,
    rewardConfig: response.body.rewardConfig
  };
};

const fetchMyQuestStageRequested = () => ({
  type: ActionType.FETCH_MY_QUEST_STAGES_REQUESTED,
  payload: undefined
});

const fetchMyQuestStageSuccess = data => ({
  type: ActionType.FETCH_MY_QUEST_STAGES_SUCCESS,
  payload: {
    data
  }
});

const fetchMyQuestStageFailure = error => ({
  type: ActionType.FETCH_MY_QUEST_STAGES_FAILURE,
  payload: {
    error
  }
});

export const fetchMyQuestStages = () => async dispatch => {
  dispatch(fetchMyQuestStageRequested());
  const response = await api.getMyQuestStages({});

  if (response.status === 'error') {
    dispatch(fetchMyQuestStageFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchMyQuestStageSuccess(response.body));
  return {
    data: response.body
  };
};

const refreshQuestRequested = () => ({
  type: ActionType.REFRESH_QUEST_REQUESTED,
  payload: undefined
});

const refreshQuestSuccess = () => ({
  type: ActionType.REFRESH_QUEST_SUCCESS,
  payload: undefined
});

const refreshQuestFailure = error => ({
  type: ActionType.REFRESH_QUEST_FAILURE,
  payload: {
    error
  }
});

export const refreshQuest = questId => async dispatch => {
  dispatch(refreshQuestRequested());
  const response = await api.refreshQuest({
    questId
  });

  if (response.status === 'error') {
    dispatch(refreshQuestFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(refreshQuestSuccess());
  return {};
};

const claimQuestRewardRequested = () => ({
  type: ActionType.CLAIM_QUEST_REWARD_REQUESTED,
  payload: undefined
});

const claimQuestRewardSuccess = () => ({
  type: ActionType.CLAIM_QUEST_REWARD_SUCCESS,
  payload: undefined
});

const claimQuestRewardFailure = error => ({
  type: ActionType.CLAIM_QUEST_REWARD_FAILURE,
  payload: {
    error
  }
});

export const claimQuestReward = stage => async dispatch => {
  dispatch(claimQuestRewardRequested());
  const response = await api.claimQuestReward({
    stage
  });

  if (response.status === 'error') {
    dispatch(claimQuestRewardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(claimQuestRewardSuccess());
  return {};
};

const fetchQuestLeaderboardRequested = () => ({
  type: ActionType.FETCH_QUEST_LEADERBOARD_REQUESTED,
  payload: undefined
});

const fetchQuestLeaderboardSuccess = data => ({
  type: ActionType.FETCH_QUEST_LEADERBOARD_SUCCESS,
  payload: {
    data
  }
});

const fetchQuestLeaderboardFailure = error => ({
  type: ActionType.FETCH_QUEST_LEADERBOARD_FAILURE,
  payload: {
    error
  }
});

export const fetchQuestLeaderboard = (page, limit) => async dispatch => {
  dispatch(fetchQuestLeaderboardRequested());
  const response = await api.getQuestLeaderboard({
    page,
    limit
  });

  if (response.status === 'error') {
    dispatch(fetchQuestLeaderboardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchQuestLeaderboardSuccess(response.body));
  return {
    data: response.body
  };
};

const fetchDailyQuestRewardRequested = () => ({
  type: ActionType.FETCH_DAILY_QUEST_REWARD_REQUESTED,
  payload: undefined
});

const fetchDailyQuestRewardSuccess = data => ({
  type: ActionType.FETCH_DAILY_QUEST_REWARD_SUCCESS,
  payload: {
    data
  }
});

const fetchDailyQuestRewardFailure = error => ({
  type: ActionType.FETCH_DAILY_QUEST_REWARD_FAILURE,
  payload: {
    error
  }
});

export const fetchDailyQuestReward = () => async dispatch => {
  dispatch(fetchDailyQuestRewardRequested());
  const response = await api.getDailyQuestReward({});

  if (response.status === 'error') {
    dispatch(fetchDailyQuestRewardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchDailyQuestRewardSuccess(response.body));
  return {
    data: response.body
  };
};

const fetchQuestBoxRequested = () => ({
  type: ActionType.FETCH_QUEST_BOX_REQUESTED,
  payload: undefined
});

const fetchQuestBoxSuccess = data => ({
  type: ActionType.FETCH_QUEST_BOX_SUCCESS,
  payload: {
    data
  }
});

const fetchQuestBoxFailure = error => ({
  type: ActionType.FETCH_QUEST_BOX_FAILURE,
  payload: {
    error
  }
});

export const fetchQuestBox = () => async dispatch => {
  dispatch(fetchQuestBoxRequested());
  const response = await api.getQuestBox({});

  if (response.status === 'error') {
    dispatch(fetchQuestBoxFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchQuestBoxSuccess(response.body));
  return {
    data: response.body
  };
};

const claimDailyQuestRewardRequested = () => ({
  type: ActionType.CLAIM_DAILY_QUEST_REWARD_REQUESTED,
  payload: undefined
});

const claimDailyQuestRewardSuccess = data => ({
  type: ActionType.CLAIM_DAILY_QUEST_REWARD_SUCCESS,
  payload: {
    success: data
  }
});

const claimDailyQuestRewardFailure = error => ({
  type: ActionType.CLAIM_DAILY_QUEST_REWARD_FAILURE,
  payload: {
    error
  }
});

export const prepareClaimDailyQuestReward = () => async dispatch => {
  dispatch(claimDailyQuestRewardRequested());
  const response = await api.claimDailyQuestReward({});

  if (response.status === 'error') {
    dispatch(claimDailyQuestRewardFailure(response.error));
    return {
      error: response.error
    };
  }

  return {
    data: response.body
  };
};
export const claimDailyQuestReward = args => async (dispatch, getState) => {
  const {
    data: preparedData,
    error: preparedError
  } = args;

  if (preparedError || preparedData === undefined) {
    const _error = preparedError || AppError.UNNKOWN;

    claimDailyQuestRewardFailure(_error);
    return {
      error: _error,
      success: false
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(claimDailyQuestRewardFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN,
      success: false
    };
  }

  const {
    id,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse
  } = preparedData;
  const {
    error: confirmError
  } = await ethereum.claimGiftBox({
    id,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse,
    accountAddress: accounts[0]
  });

  if (confirmError !== EipError.NO_ERROR) {
    dispatch(claimDailyQuestRewardFailure(confirmError));
    return {
      error: confirmError,
      success: false
    };
  }

  dispatch(claimDailyQuestRewardSuccess(true));
  return {
    success: true
  };
};

const claimWheelTicketRequested = () => ({
  type: ActionType.CLAIM_WHEEL_TICKET_REQUESTED,
  payload: undefined
});

const claimWheelTicketSuccess = data => ({
  type: ActionType.CLAIM_WHEEL_TICKET_SUCCESS,
  payload: {
    success: data
  }
});

const claimWheelTicketFailure = error => ({
  type: ActionType.CLAIM_WHEEL_TICKET_FAILURE,
  payload: {
    error
  }
});

export const claimWheelTicket = () => async (dispatch, getState) => {
  dispatch(claimWheelTicketRequested());
  const signSignatureRes = await api.signSignatureClaimWheelTicket({});

  if (signSignatureRes.status === 'error') {
    dispatch(claimWheelTicketFailure(signSignatureRes.error));
    return {
      error: signSignatureRes.error
    };
  }

  const accounts = getState().connectWallet.accounts;

  if (!accounts || accounts.length === 0) {
    dispatch(claimWheelTicketFailure(AppError.NOT_LOGGED_IN));
    return {
      error: AppError.NOT_LOGGED_IN
    };
  }

  const {
    id,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse
  } = signSignatureRes.body;
  const {
    error: confirmError
  } = await ethereum.claimWheelTicket({
    id,
    boxType: boxTypeResponse,
    price,
    paymentErc20,
    expiredAt,
    signature: signatureResponse,
    accountAddress: accounts[0]
  });

  if (confirmError !== EipError.NO_ERROR) {
    dispatch(claimWheelTicketFailure(confirmError));
    return {
      error: confirmError
    };
  }

  dispatch(claimWheelTicketSuccess(true));
  return {};
};

const claimStreakRewardRequested = () => ({
  type: ActionType.CLAIM_STREAK_REWARD_REQUESTED,
  payload: undefined
});

const claimStreakRewardSuccess = data => ({
  type: ActionType.CLAIM_STREAK_REWARD_SUCCESS,
  payload: {
    success: data
  }
});

const claimStreakRewardFailure = error => ({
  type: ActionType.CLAIM_STREAK_REWARD_FAILURE,
  payload: {
    error
  }
});

export const claimStreakReward = () => async dispatch => {
  dispatch(claimStreakRewardRequested());
  const response = await api.claimStreakReward({});

  if (response.status === 'error') {
    dispatch(claimStreakRewardFailure(response.error));
    return {
      success: false,
      error: response.error
    };
  }

  dispatch(claimStreakRewardSuccess(response.body.success));
  return {
    success: response.body.success
  };
};

const claimQuestBoxRequested = () => ({
  type: ActionType.CLAIM_QUEST_BOX_REQUESTED,
  payload: undefined
});

const claimQuestBoxSuccess = data => ({
  type: ActionType.CLAIM_QUEST_BOX_SUCCESS,
  payload: {
    success: data
  }
});

const claimQuestBoxFailure = error => ({
  type: ActionType.CLAIM_QUEST_BOX_FAILURE,
  payload: {
    error
  }
});

export const claimQuestBox = () => async dispatch => {
  dispatch(claimQuestBoxRequested());
  const response = await api.postClaimQuestBox({});

  if (response.status === 'error') {
    dispatch(claimQuestBoxFailure(response.error));
    return {
      success: false,
      error: response.error
    };
  }

  dispatch(claimQuestBoxSuccess(response.body.success));
  return {
    success: response.body.success
  };
};

const fetchCoinbaseLootEventConfigsRequested = () => ({
  type: ActionType.FETCH_COINBASE_LOOT_EVENT_CONFIGS_REQUESTED,
  payload: undefined
});

const fetchCoinbaseLootEventConfigsFailure = error => ({
  type: ActionType.FETCH_COINBASE_LOOT_EVENT_CONFIGS_FAILURE,
  payload: {
    error
  }
});

const fetchCoinbaseLootEventConfigsSuccess = data => ({
  type: ActionType.FETCH_COINBASE_LOOT_EVENT_CONFIGS_SUCCESS,
  payload: {
    data
  }
});

export const fetchCoinbaseLootEventConfigs = () => async dispatch => {
  dispatch(fetchCoinbaseLootEventConfigsRequested());
  const response = await api.getCoinbaseLootConfigs({});

  if (response.status === 'error') {
    dispatch(fetchCoinbaseLootEventConfigsFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchCoinbaseLootEventConfigsSuccess(data));
  return {
    data
  };
};

const fetchTradingEventSeasonRequested = () => ({
  type: ActionType.FETCH_TRADING_EVENT_SEASON_REQUESTED,
  payload: undefined
});

const fetchTradingEventSeasonFailure = error => ({
  type: ActionType.FETCH_TRADING_EVENT_SEASON_FAILURE,
  payload: {
    error
  }
});

const fetchTradingEventSeasonSuccess = (data, config) => ({
  type: ActionType.FETCH_TRADING_EVENT_SEASON_SUCCESS,
  payload: {
    data,
    config
  }
});

export const fetchTradingEventSeason = version => async dispatch => {
  dispatch(fetchTradingEventSeasonRequested());
  const response = await api.getTradingEventSeasonData({
    version
  });

  if (response.status === 'error') {
    dispatch(fetchTradingEventSeasonFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data,
    config
  } = response.body;
  dispatch(fetchTradingEventSeasonSuccess(data, config));
  return {
    data
  };
};

const fetchClaimTradingEventDailyChallengeRequested = () => ({
  type: ActionType.FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_REQUESTED,
  payload: undefined
});

const fetchClaimTradingEventDailyChallengeFailure = error => ({
  type: ActionType.FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_FAILURE,
  payload: {
    error
  }
});

const fetchClaimTradingEventDailyChallengeSuccess = data => ({
  type: ActionType.FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_SUCCESS,
  payload: {
    data
  }
});

export const fetchClaimTradingEventDailyChallenge = type => async dispatch => {
  dispatch(fetchClaimTradingEventDailyChallengeRequested());
  const response = await api.putClaimTradingEventDailyChallenge({
    type
  });

  if (response.status === 'error') {
    dispatch(fetchClaimTradingEventDailyChallengeFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchClaimTradingEventDailyChallengeSuccess(data));
  return {
    data
  };
};

const fetchClaimPreTradingEventDailyRewardRequested = () => ({
  type: ActionType.FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_REQUESTED,
  payload: undefined
});

const fetchClaimPreTradingEventDailyRewardFailure = error => ({
  type: ActionType.FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_FAILURE,
  payload: {
    error
  }
});

const fetchClaimPreTradingEventDailyRewardSuccess = data => ({
  type: ActionType.FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_SUCCESS,
  payload: {
    data
  }
});

export const fetchClaimPreTradingEventDailyReward = () => async dispatch => {
  dispatch(fetchClaimPreTradingEventDailyRewardRequested());
  const response = await api.putClaimPreTradingEventDailyReward({});

  if (response.status === 'error') {
    dispatch(fetchClaimPreTradingEventDailyRewardFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchClaimPreTradingEventDailyRewardSuccess(data));
  return {
    data
  };
};

const fetchClaimTradingEventCashbackRequested = () => ({
  type: ActionType.FETCH_CLAIM_TRADING_EVENT_CASHBACK_REQUESTED,
  payload: undefined
});

const fetchClaimTradingEventCashbackFailure = error => ({
  type: ActionType.FETCH_CLAIM_TRADING_EVENT_CASHBACK_FAILURE,
  payload: {
    error
  }
});

const fetchClaimTradingEventCashbackSuccess = data => ({
  type: ActionType.FETCH_CLAIM_TRADING_EVENT_CASHBACK_SUCCESS,
  payload: {
    data
  }
});

export const fetchClaimTradingEventCashback = milestoneId => async dispatch => {
  dispatch(fetchClaimTradingEventCashbackRequested());
  const result = await api.putClaimTradingEventCashback({
    milestoneId
  });

  if (result.status === 'error') {
    dispatch(fetchClaimTradingEventCashbackFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data
  } = result.body;
  dispatch(fetchClaimTradingEventCashbackSuccess(data));
  return {
    data
  };
};

const fetchClaimAllTradingEventRewardsRequested = () => ({
  type: ActionType.FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_REQUESTED,
  payload: undefined
});

const fetchClaimAllTradingEventRewardsFailure = error => ({
  type: ActionType.FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_FAILURE,
  payload: {
    error
  }
});

const fetchClaimAllTradingEventRewardsSuccess = data => ({
  type: ActionType.FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_SUCCESS,
  payload: {
    data
  }
});

export const fetchClaimAllTradingEventRewards = () => async dispatch => {
  dispatch(fetchClaimAllTradingEventRewardsRequested());
  const result = await api.putClaimAllTradingEventRewards({});

  if (result.status === 'error') {
    dispatch(fetchClaimAllTradingEventRewardsFailure(result.error));
    return {
      error: result.error
    };
  }

  const {
    data
  } = result.body;
  dispatch(fetchClaimAllTradingEventRewardsSuccess(data));
  return {
    data
  };
};

const fetchTradingEventLeaderboardRequested = () => ({
  type: ActionType.FETCH_TRADING_EVENT_LEADERBOARD_REQUESTED,
  payload: undefined
});

const fetchTradingEventLeaderboardSuccess = data => ({
  type: ActionType.FETCH_TRADING_EVENT_LEADERBOARD_SUCCESS,
  payload: {
    data
  }
});

const fetchTradingEventLeaderboardFailure = error => ({
  type: ActionType.FETCH_TRADING_EVENT_LEADERBOARD_FAILURE,
  payload: {
    error
  }
});

export const fetchTradingEventLeaderboard = (seasonId, page, size) => async dispatch => {
  dispatch(fetchTradingEventLeaderboardRequested());
  const response = await api.getTradingEventLeaderboard({
    seasonId,
    from: page,
    size
  });

  if (response.status === 'error') {
    dispatch(fetchTradingEventLeaderboardFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(fetchTradingEventLeaderboardSuccess(response.body));
  return {
    data: response.body
  };
};

const fetchPreviousTradingEventSeasonRequested = () => ({
  type: ActionType.FETCH_PREVIOUS_TRADING_EVENT_SEASON_REQUESTED,
  payload: undefined
});

const fetchPreviousTradingEventSeasonFailure = error => ({
  type: ActionType.FETCH_PREVIOUS_TRADING_EVENT_SEASON_FAILURE,
  payload: {
    error
  }
});

const fetchPreviousTradingEventSeasonSuccess = (data, config) => ({
  type: ActionType.FETCH_PREVIOUS_TRADING_EVENT_SEASON_SUCCESS,
  payload: {
    data,
    config
  }
});

export const fetchPreviousTradingEventSeason = (version, seasonId) => async dispatch => {
  dispatch(fetchPreviousTradingEventSeasonRequested());
  const response = await api.getTradingEventSeasonData({
    version,
    seasonId
  });

  if (response.status === 'error') {
    dispatch(fetchPreviousTradingEventSeasonFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data,
    config
  } = response.body;
  dispatch(fetchPreviousTradingEventSeasonSuccess(data, config));
  return {
    data
  };
};

const fetchAdsPopupConfigRequested = () => ({
  type: ActionType.FETCH_ADS_POPUP_CONFIG_REQUESTED,
  payload: undefined
});

const fetchAdsPopupConfigSuccess = data => ({
  type: ActionType.FETCH_ADS_POPUP_CONFIG_SUCCESS,
  payload: {
    data
  }
});

const fetchAdsPopupConfigFailure = error => ({
  type: ActionType.FETCH_ADS_POPUP_CONFIG_FAILURE,
  payload: {
    error
  }
});

export const fetchAdsPopupConfig = () => async dispatch => {
  dispatch(fetchAdsPopupConfigRequested());
  const response = await api.getPopupConfig({
    name: 'mkp_popup',
    raw: true
  });

  if (response.status === 'error') {
    dispatch(fetchAdsPopupConfigFailure(response.error));
    return undefined;
  }

  dispatch(fetchAdsPopupConfigSuccess(response.body));
  return response.body;
};

const fetchSamSungEventRequested = () => ({
  type: ActionType.FETCH_SAMSUNG_EVENT_REQUESTED,
  payload: undefined
});

const fetchSamSungEventSuccess = data => ({
  type: ActionType.FETCH_SAMSUNG_EVENT_SUCCESS,
  payload: {
    data
  }
});

const fetchSamSungEventFailure = error => ({
  type: ActionType.FETCH_SAMSUNG_EVENT_FAILURE,
  payload: {
    error
  }
});

export const fetchSamSungEvent = () => async dispatch => {
  dispatch(fetchSamSungEventRequested());
  const response = await api.getSamsungEvent({});

  if (response.status === 'error') {
    dispatch(fetchSamSungEventFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchSamSungEventSuccess(data));
  return {
    data
  };
};

const fetchGiftmasWheelRequested = () => ({
  type: ActionType.FETCH_GIFTMAS_WHEEL_REQUESTED,
  payload: undefined
});

const fetchGiftmasWheelSuccess = data => ({
  type: ActionType.FETCH_GIFTMAS_WHEEL_SUCCESS,
  payload: {
    data
  }
});

const fetchGiftmasWheelFailure = error => ({
  type: ActionType.FETCH_GIFTMAS_WHEEL_FAILURE,
  payload: {
    error
  }
});

export const fetchGiftmasWheel = () => async dispatch => {
  dispatch(fetchGiftmasWheelRequested());
  const response = await api.getGiftmasWheel({});

  if (response.status === 'error') {
    dispatch(fetchGiftmasWheelFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchGiftmasWheelSuccess(data));
  return {
    data
  };
};

const fetchWheelRewardRequested = () => ({
  type: ActionType.FETCH_WHEEL_REWARD_REQUESTED,
  payload: undefined
});

const fetchWheelRewardSuccess = data => ({
  type: ActionType.FETCH_WHEEL_REWARD_SUCCESS,
  payload: {
    data
  }
});

const fetchWheelRewardFailure = error => ({
  type: ActionType.FETCH_WHEEL_REWARD_FAILURE,
  payload: {
    error
  }
});

export const fetchWheelReward = () => async dispatch => {
  dispatch(fetchWheelRewardRequested());
  const response = await api.getWheelReward({});

  if (response.status === 'error') {
    dispatch(fetchWheelRewardFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchWheelRewardSuccess(data));
  return {
    data
  };
};

const fetchLogoCombineRequested = () => ({
  type: ActionType.FETCH_LOGO_COMBINE_REQUESTED,
  payload: undefined
});

const fetchLogoCombineSuccess = data => ({
  type: ActionType.FETCH_LOGO_COMBINE_SUCCESS,
  payload: {
    data
  }
});

const fetchLogoCombineFailure = error => ({
  type: ActionType.FETCH_LOGO_COMBINE_FAILURE,
  payload: {
    error
  }
});

export const fetchLogoCombine = () => async dispatch => {
  dispatch(fetchLogoCombineRequested());
  const response = await api.postCombineLogo({});

  if (response.status === 'error') {
    dispatch(fetchLogoCombineFailure(response.error));
    return {
      error: response.error
    };
  }

  const {
    data
  } = response.body;
  dispatch(fetchLogoCombineSuccess(data));
  return {
    data
  };
};
export const fetchThetanWorldConfig = () => async dispatch => {
  dispatch(getThetanWorldConfigRequested());
  const response = await api.getThetanWorldConfigApi({});

  if (response.status === 'error') {
    dispatch(getThetanWorldConfigFailure(response.error));
    return {
      error: response.error
    };
  }

  dispatch(getThetanWorldConfigSuccess(response.body));
  return {
    data: response.body
  };
};

const getThetanWorldConfigRequested = () => ({
  type: ActionType.FETCH_THETAN_WORLD_CONFIG_REQUESTED,
  payload: undefined
});

const getThetanWorldConfigSuccess = data => ({
  type: ActionType.FETCH_THETAN_WORLD_CONFIG_SUCCESS,
  payload: {
    data
  }
});

const getThetanWorldConfigFailure = error => ({
  type: ActionType.FETCH_THETAN_WORLD_CONFIG_FAILURE,
  payload: {
    error
  }
});